/**
 * NuevaReservaWizard — Stepper de 4 pasos para crear una reservación.
 *  1. Salida   — Tipo (pasadía/alquiler) + fecha + selección
 *  2. Pasajeros — Cliente + cantidades + agencia (opcional)
 *  3. Pago     — Forma de pago + referencia/comprobante + descuento comercial (admin)
 *  4. Confirmar — Resumen + crear
 *
 * Tras crear muestra el código generado + opciones (copiar link Bold, cerrar).
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Stepper, Step, StepLabel, Stack, Button, TextField, MenuItem,
  Chip, Alert, CircularProgress, IconButton, Tooltip, Divider, Switch,
  Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SlideOver from '../common/SlideOver';
import useToast from '../common/useToast';
import {
  API_BASE, getAuthHeaders, getEmpresaId, getUserRole, formatPrecio, todayLocal,
} from './shared/format';

const STEPS = ['Salida', 'Pasajeros', 'Pago', 'Confirmar'];

const FORMA_PAGO = [
  { value: 'efectivo',      label: 'Efectivo' },
  { value: 'nequi',         label: 'Nequi',            requiereCuentaDestino: true },
  { value: 'wompi',         label: 'Wompi',            requiereCuentaDestino: true },
  { value: 'transferencia', label: 'Transferencia',    requiereCuentaDestino: true },
  { value: 'tarjeta',       label: 'Tarjeta (datáfono)', requiereCuentaDestino: true },
  { value: 'credito',       label: 'Crédito agencia',  requiereAgencia: true },
  { value: 'muelle',        label: 'Pago en muelle',   requiereAgencia: true },
  { value: 'bold_link',     label: 'Link Bold',        requiereCuentaDestino: true },
  // 'cortesia' aparece solo cuando precio_total = 0 por cortesías totales.
  // En ese caso el wizard auto-selecciona esta opción y oculta las demás.
  { value: 'cortesia',      label: 'Cortesía (sin cobro)', soloCortesiaTotal: true },
];

// Mapeo forma_pago → tipo de cuenta destino. Cuando el operador elige uno
// de estos métodos, el wizard filtra las cuentas_destino_pago por este tipo.
const CUENTA_TIPO_POR_FORMA = {
  nequi: 'nequi',
  wompi: 'wompi',
  transferencia: 'bancolombia',
  bold_link: 'bold',
  tarjeta: 'datafono',
};

// Una transferencia puede entrar por cualquiera de los bancos de Entretur, no
// solo por Bancolombia. `CUENTA_TIPO_POR_FORMA` sigue dando el tipo principal
// —para el texto del aviso y para saber si el método exige cuenta destino— y
// esto dice cuáles tipos aceptar al filtrar el selector. Sin esta lista, una
// cuenta de Banco de Bogotá existe en la configuración pero no aparece nunca
// como destino de una transferencia.
const CUENTA_TIPOS_ACEPTADOS = {
  transferencia: ['bancolombia', 'banco_bogota'],
};
const tiposDeCuentaPara = (forma) =>
  CUENTA_TIPOS_ACEPTADOS[forma] || [CUENTA_TIPO_POR_FORMA[forma]].filter(Boolean);

// Etiqueta del selector según el método (para que diga "billetera", "cuenta
// bancaria", etc. en vez de un genérico).
const CUENTA_LABEL_POR_FORMA = {
  nequi: { label: 'Cuenta destino *', placeholder: 'Selecciona la billetera Nequi' },
  wompi: { label: 'Cuenta Wompi *',    placeholder: 'Selecciona la cuenta Wompi' },
  transferencia: { label: 'Cuenta destino (banco) *', placeholder: 'Selecciona la cuenta bancaria' },
  bold_link: { label: 'Cuenta Bold *', placeholder: 'Selecciona la cuenta Bold' },
  tarjeta: { label: 'Datáfono *',      placeholder: 'Selecciona el datáfono' },
};

export default function NuevaReservaWizard({ open, onClose, onCreated }) {
  const empresaId = getEmpresaId();
  const userRole = getUserRole();
  const { toast, ToastContainer } = useToast();

  const [step, setStep] = useState(0);
  const [tipo, setTipo] = useState('pasadia'); // pasadia | alquiler
  const [fecha, setFecha] = useState(todayLocal());
  const [salidaId, setSalidaId] = useState('');
  const [alquilerId, setAlquilerId] = useState('');

  const [salidasDelDia, setSalidasDelDia] = useState([]);
  // Si el cierre de muelle de esa fecha ya está firmado, ese día no admite
  // ventas nuevas. El backend ya lo rechaza, pero lo hace al CREAR: el
  // vendedor alcanzaba a llenar nombre, pasajeros y forma de pago para recién
  // ahí toparse con el error. Se consulta al elegir la fecha.
  const [diaCerrado, setDiaCerrado] = useState(null);
  const [alquileres, setAlquileres] = useState([]);
  const [agencias, setAgencias] = useState([]);
  const [adminsEmpresa, setAdminsEmpresa] = useState([]);
  const [comisionesAgPaquete, setComisionesAgPaquete] = useState({});
  const [cargando, setCargando] = useState(false);

  const [form, setForm] = useState({
    nombre_cliente: '', numero_whatsapp: '', correo: '',
    adultos: 1, ninos: 0, bebes: 0, cortesias: 0,
    // Migración 084: cortesías diferenciadas (no pagan, no generan comisión)
    cortesias_adultos: 0, cortesias_ninos: 0,
    cortesias_motivo: '', cortesias_autorizada_por_nombre: '',
    agencia_id: '', notas: '', requerimientos_especiales: '',
    canal_reserva: 'web_directo',
  });

  const [pago, setPago] = useState({
    forma_pago: 'efectivo',
    referencia_pago: '',
    comprobante_url: '',
    // Pago mixto: el cliente entrega a la agencia con varios métodos a la vez.
    // Cuando está activo, cada línea genera su propio voucher (uno por método).
    mixto_check: false,
    lineas_mixto: [
      { metodo_pago: 'transferencia', monto: '', referencia: '' },
      { metodo_pago: 'efectivo',      monto: '', referencia: '' },
    ],
    // Comisión anticipada: cuando la agencia ya cobró su comisión al cliente
    // y por lo tanto Entretur NO se la debe. Puede ser total o parcial.
    comision_anticipada_check: false,
    comision_anticipada: 0,
    // Transferencia parcial: la agencia abona parte y el saldo lo asume el
    // turista en muelle o queda como deuda contable (cupo) de la agencia.
    // Solo aplica cuando forma_pago === 'transferencia' y hay agencia.
    transferencia_parcial_check: false,
    monto_transferido: 0,
    saldo_a_cargo_de: 'muelle',  // 'muelle' | 'agencia'
    // Descuento que la agencia regala al turista — sale de SU comisión.
    // Distinto del descuento_comercial (que sale del bolsillo de Entretur).
    // Tope = comisionTotal − comision_anticipada (com_ant + desc_com ≤ com_total).
    descuento_comision_check: false,
    descuento_comision: 0,
    // Cuenta destino del pago: requerido cuando forma_pago='nequi' (qué
    // billetera recibe). FK a cuentas_destino_pago (Nequi Entretur / NC / DC).
    cuenta_destino_id: '',
    // Pago directo del cliente a Entretur (flujo paralelo al agencia↔Entretur).
    // Ej: el cliente le paga a la agencia solo la comisión y le transfiere el
    // resto (o parte) directo a Entretur. Este bloque NO reemplaza el pago
    // agencia — coexisten. Al enviar la reserva se crea un PagoReservacion
    // con origen='pago_directo_cliente' que reduce el saldo pendiente.
    pago_directo_check: false,
    pago_directo_monto: 0,
    pago_directo_metodo: 'transferencia',
    pago_directo_cuenta_destino_id: '',
    pago_directo_referencia: '',
    pago_directo_comprobante_url: '',
    // Pagos directos ADICIONALES (mixto con agencia). Cada objeto:
    // { monto, metodo_pago, cuenta_destino_id, referencia, comprobante_url }
    // Al enviar, se combina el pago_directo_* principal + estos en un array
    // que va al backend como `pagos_directos_entretur`.
    pagos_directos_extras: [],
    // Migración 111 · Saldo dividido — cuando el saldo restante se reparte
    // entre CxC agencia + cobro en muelle (Ej: $300k CxC + $200k muelle).
    // Al enviar, va al backend como `saldo_dividido: {cxc_agencia_monto, saldo_muelle_monto}`.
    saldo_dividido_check: false,
    saldo_dividido_cxc: 0,
    saldo_dividido_muelle: 0,
    // Con agencia, `forma_pago` se auto-infiere desde la distribución. Este
    // flag permite al operador tomar el control manual (ejemplo: casos
    // atípicos donde la inferencia no coincide con lo comercial). Cuando es
    // false (default) el selector "¿Cómo pagó a la agencia?" se oculta y se
    // muestra solo un chip "Auto" en el accordion de detalles técnicos.
    override_manual_forma_pago: false,
  });

  const [creando, setCreando] = useState(false);
  const [resultado, setResultado] = useState(null);
  // Cuentas destino (Nequi Entretur / NC / DC, etc.). Se cargan al abrir el
  // wizard y se comparten entre StepPago (selector) y StepConfirmar (resumen).
  const [cuentasDestino, setCuentasDestino] = useState(null);

  useEffect(() => {
    if (!open) return;
    const empresaId = getEmpresaId();
    fetch(`${API_BASE}/turismo/cuentas-destino-pago?empresa_id=${empresaId}`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : [])
      .then(setCuentasDestino)
      .catch(() => setCuentasDestino([]));
  }, [open]);

  // Cargar maestros al abrir
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [aRes, alqRes, comRes, admRes] = await Promise.all([
          fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}&solo_activas=true`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE}/turismo/alquiler-embarcacion?empresa_id=${empresaId}&solo_activos=true`, { headers: getAuthHeaders() }).catch(() => null),
          fetch(`${API_BASE}/turismo/comisiones-paquete?empresa_id=${empresaId}`, { headers: getAuthHeaders() }).catch(() => null),
          fetch(`${API_BASE}/admin/usuarios?empresa_id=${empresaId}&estado=aprobado`, { headers: getAuthHeaders() }).catch(() => null),
        ]);
        setAgencias((await aRes.json()) || []);
        if (alqRes) setAlquileres((await alqRes.json()) || []);
        if (comRes) {
          const data = await comRes.json();
          const idx = {};
          (Array.isArray(data) ? data : []).forEach(o => {
            if (!o.activo) return;
            if (!idx[o.agencia_id]) idx[o.agencia_id] = {};
            idx[o.agencia_id][o.paquete_id] = o;
          });
          setComisionesAgPaquete(idx);
        }
        if (admRes) {
          const users = await admRes.json();
          setAdminsEmpresa((Array.isArray(users) ? users : []).filter(u => u.role === 'admin' || u.role === 'superadmin'));
        }
      } catch (e) { console.error(e); }
    })();
  }, [open, empresaId]);

  // ¿Este día ya se cerró? Se pregunta para cualquier tipo de reserva: el
  // cierre bloquea el día completo, no solo las pasadías.
  useEffect(() => {
    if (!open || !fecha) { setDiaCerrado(null); return; }
    let vigente = true;
    fetch(`${API_BASE}/turismo/muelle/estado-dia?empresa_id=${empresaId}&fecha=${fecha}`,
      { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(x => { if (vigente) setDiaCerrado(x && x.cerrado ? x : null); })
      // Si la consulta falla no se bloquea la venta: el guardián del backend
      // sigue ahí. Esto es comodidad, no la barrera.
      .catch(() => { if (vigente) setDiaCerrado(null); });
    return () => { vigente = false; };
  }, [open, fecha, empresaId]);

  // Cargar salidas del día cuando cambia fecha
  useEffect(() => {
    if (!open || tipo !== 'pasadia' || !fecha) return;
    setCargando(true);
    // No filtramos por estado en el backend porque match es exacto y
    // 'programada' y 'activa' son ambos vendibles. Filtramos terminales acá.
    fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}&fecha_desde=${fecha}&fecha_hasta=${fecha}`,
      { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.salidas || []);
        const ESTADOS_TERMINALES = ['suspendida', 'cancelada', 'completada', 'no_show'];
        setSalidasDelDia(list.filter(s => {
          if (ESTADOS_TERMINALES.includes(s.estado)) return false;
          // Capacidad efectiva (descontando lanchas compartidas si aplica).
          // Si no viene el campo efectivo, fallback a la capacidad total física.
          const disp = (s.asientos_disponibles_efectivos != null)
            ? s.asientos_disponibles_efectivos
            : ((s.capacidad_total || s.capacidad_override || 0) - (s.asientos_reservados || 0));
          return disp > 0;
        }));
      })
      .catch(() => setSalidasDelDia([]))
      .finally(() => setCargando(false));
  }, [open, tipo, fecha, empresaId]);

  const salidaSel = salidasDelDia.find(s => s.id === parseInt(salidaId));
  const alquilerSel = alquileres.find(a => a.id === parseInt(alquilerId));

  // Cálculo de precio — Migración 097/098 (Fases A+B):
  //   - Sin agencia (venta directa Entretur) → precio = PVP directa
  //   - Con agencia                          → precio = neto + comisión absoluta
  //   - Override de la salida                → prevalece sobre cualquier cálculo
  //   - Bebés siempre $0 (regla de negocio confirmada por el usuario)
  const precioTotal = useMemo(() => {
    if (tipo === 'alquiler') {
      return Number(alquilerSel?.precio || 0) * (Number(form.adultos) || 1);
    }
    if (!salidaSel) return 0;
    const ag = agencias.find(a => String(a.id) === String(form.agencia_id));
    // comisionesAgPaquete se indexa como objeto anidado {agencia_id: {paquete_id: cfg}}
    // (ver setComisionesAgPaquete arriba). Antes esta línea usaba la clave string
    // "${agId}_${paqId}" que NUNCA matcheaba → cfg=undefined → comisión=0 →
    // precio_unit calculaba solo el neto sin sumar comisión → total = 4×220k = 880k
    // cuando debía ser 4×360k = 1.440k. Bug crítico corregido.
    const cfg = ag && (comisionesAgPaquete[ag.id] || {})[salidaSel.paquete_id];

    // Comisión absoluta por pax si hay agencia con config
    const comA = cfg && cfg.valor_comision_adulto != null ? Number(cfg.valor_comision_adulto) : 0;
    const comN = cfg && cfg.valor_comision_nino != null ? Number(cfg.valor_comision_nino) : 0;

    // Precio unitario adulto
    let pa;
    if (salidaSel.precio_adulto_override != null) {
      pa = Number(salidaSel.precio_adulto_override);
    } else if (ag) {
      const neto = Number(
        salidaSel.precio_neto_adulto
        ?? salidaSel.paquete?.precio_neto_adulto
        ?? salidaSel.precio_adulto_base
        ?? salidaSel.precio_adulto
        ?? 0
      );
      pa = neto + comA;
    } else {
      pa = Number(
        salidaSel.precio_pvp_adulto_directa
        ?? salidaSel.paquete?.precio_pvp_adulto_directa
        ?? salidaSel.precio_adulto_base
        ?? salidaSel.precio_adulto
        ?? 0
      );
    }

    // Precio unitario niño
    let pn;
    if (salidaSel.precio_nino_override != null) {
      pn = Number(salidaSel.precio_nino_override);
    } else if (ag) {
      const neto = Number(
        salidaSel.precio_neto_nino
        ?? salidaSel.paquete?.precio_neto_nino
        ?? salidaSel.precio_nino_base
        ?? salidaSel.precio_nino
        ?? 0
      );
      pn = neto + comN;
    } else {
      pn = Number(
        salidaSel.precio_pvp_nino_directa
        ?? salidaSel.paquete?.precio_pvp_nino_directa
        ?? salidaSel.precio_nino_base
        ?? salidaSel.precio_nino
        ?? 0
      );
    }

    const pb = 0;  // Bebés siempre gratis (regla de negocio)
    const pagantesAdultos = Math.max(0, (Number(form.adultos) || 0) - (Number(form.cortesias_adultos) || 0));
    const pagantesNinos = Math.max(0, (Number(form.ninos) || 0) - (Number(form.cortesias_ninos) || 0));
    return pagantesAdultos * pa + pagantesNinos * pn + (Number(form.bebes) || 0) * pb;
  }, [tipo, salidaSel, alquilerSel, form.adultos, form.ninos, form.bebes,
      form.cortesias_adultos, form.cortesias_ninos, form.agencia_id, agencias, comisionesAgPaquete]);

  // Detectar cortesía 100%: todos los pax pagantes son cortesía (precio_total=0
  // por cortesías otorgadas). Auto-selecciona forma_pago='cortesia' y bloquea
  // el dropdown. Si después el operador agrega un pax pagante, vuelve al
  // default 'efectivo' para que pueda elegir manualmente otra vez.
  const totalCortesias = (Number(form.cortesias_adultos) || 0) + (Number(form.cortesias_ninos) || 0);
  const totalPagPax = (Number(form.adultos) || 0) + (Number(form.ninos) || 0);
  const esCortesia100 = tipo === 'pasadia'
    && totalCortesias > 0
    && totalCortesias >= totalPagPax
    && precioTotal === 0;
  useEffect(() => {
    setPago(p => {
      if (esCortesia100 && p.forma_pago !== 'cortesia') {
        return { ...p, forma_pago: 'cortesia', referencia_pago: '', comprobante_url: '' };
      }
      if (!esCortesia100 && p.forma_pago === 'cortesia') {
        return { ...p, forma_pago: 'efectivo' };
      }
      return p;
    });
  }, [esCortesia100]);

  // Saldo a favor de la agencia elegida: plata que ya entregó de más y
  // todavía no se aplica a ninguna reserva.
  const [saldoFavorAgencia, setSaldoFavorAgencia] = useState(0);
  // Si el operador decide usar el saldo a favor en esta misma reserva, la
  // aplicación va dentro de la creación: una reserva, un paso.
  const [usarSaldoFavor, setUsarSaldoFavor] = useState(false);
  useEffect(() => {
    const agId = parseInt(form.agencia_id);
    if (!agId) { setSaldoFavorAgencia(0); return; }
    fetch(`${API_BASE}/turismo/correcciones/agencias/${agId}/saldo-a-favor`,
      { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSaldoFavorAgencia(Number(d?.saldo_a_favor || 0)))
      .catch(() => setSaldoFavorAgencia(0));
    setUsarSaldoFavor(false);
  }, [form.agencia_id]);

  // Cálculo de comisión (con campos diferenciados adulto/niño cuando estén)
  const comisionTotal = useMemo(() => {
    const agId = parseInt(form.agencia_id);
    if (!agId || tipo !== 'pasadia' || !salidaSel) return 0;
    const ag = agencias.find(a => a.id === agId);
    if (!ag) return 0;
    const paquete_id = salidaSel.paquete_id || salidaSel.paquete?.id;
    const ovr = (comisionesAgPaquete[agId] || {})[paquete_id];
    const cfg = ovr || ag;
    // Migración 098: comisión absoluta por agencia-paquete. Cortesías NO
    // generan comisión → solo cuentan los pagantes.
    const adultos = Math.max(0, (Number(form.adultos) || 0) - (Number(form.cortesias_adultos) || 0));
    const ninos = Math.max(0, (Number(form.ninos) || 0) - (Number(form.cortesias_ninos) || 0));
    const va = cfg.valor_comision_adulto != null ? Number(cfg.valor_comision_adulto)
             : Number(cfg.valor_comision_fijo || 0);
    const vn = cfg.valor_comision_nino != null ? Number(cfg.valor_comision_nino)
             : Number(cfg.valor_comision_fijo || 0);
    // Fallback raro: config con solo % (no debería existir tras mig 098)
    if (va === 0 && vn === 0 && cfg.porcentaje_comision) {
      const netoA = Number(salidaSel.precio_neto_adulto ?? salidaSel.paquete?.precio_neto_adulto ?? 0);
      const netoN = Number(salidaSel.precio_neto_nino ?? salidaSel.paquete?.precio_neto_nino ?? 0);
      const pct = Number(cfg.porcentaje_comision);
      return Math.round(adultos * netoA * pct / 100 + ninos * netoN * pct / 100);
    }
    return va * adultos + vn * ninos;
  }, [form.agencia_id, form.adultos, form.ninos, form.cortesias_adultos, form.cortesias_ninos, tipo, salidaSel, agencias, comisionesAgPaquete]);

  // Validaciones por paso
  const canNext = useMemo(() => {
    if (step === 0) {
      if (diaCerrado) return false;
      if (tipo === 'pasadia') return !!salidaId;
      return !!alquilerId && !!fecha;
    }
    if (step === 1) {
      return !!form.nombre_cliente.trim() && !!form.numero_whatsapp.trim() &&
             (Number(form.adultos) + Number(form.ninos) + Number(form.bebes)) > 0;
    }
    if (step === 2) {
      // Pago mixto: validaciones específicas
      if (pago.mixto_check) {
        const lineas = pago.lineas_mixto || [];
        if (lineas.length < 2) return false;
        const metodosValidos = new Set(['efectivo', 'transferencia', 'tarjeta', 'nequi']);
        let suma = 0;
        for (const ln of lineas) {
          if (!metodosValidos.has(ln.metodo_pago)) return false;
          const m = Number(ln.monto) || 0;
          if (m <= 0) return false;
          if ((ln.metodo_pago === 'transferencia' || ln.metodo_pago === 'tarjeta')
              && !(ln.referencia || '').trim()) return false;
          suma += m;
        }
        // En mixto SIEMPRE se usa transferencia_parcial (el monto total es la suma).
        // Si transferencia_parcial_check NO está activo, suma debe ser exactamente el precio.
        // Si está activo, monto_transferido debe ser la suma (el wizard lo sincroniza).
        // Validamos coherencia básica.
        if (!pago.transferencia_parcial_check && Math.abs(suma - precioTotal) > 0.5) return false;
        if (pago.transferencia_parcial_check && suma >= precioTotal - 0.01) return false;
        if (pago.transferencia_parcial_check && !pago.saldo_a_cargo_de) return false;
        // Comisión anticipada en mixto: misma regla que en transferencia parcial
        if (pago.comision_anticipada_check) {
          const descCom = pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0;
          const tope = Math.max(comisionTotal - descCom, 0);
          const monto = Number(pago.comision_anticipada) || 0;
          if (monto <= 0 || monto > tope + 0.01) return false;
          if ((suma + monto) > precioTotal + 0.01) return false;
        }
        return true;
      }
      // Forma de pago obligatoria (sin esto la reserva quedaba en 'pendiente'
      // sin financiero — bug de Ever Armando).
      if (!pago.forma_pago) return false;
      if (pago.forma_pago === 'transferencia' && !pago.referencia_pago.trim()) return false;
      if (pago.forma_pago === 'tarjeta' && !pago.referencia_pago.trim()) return false;
      // Cualquier método con cuenta destino (Nequi, Wompi, Transferencia, Bold)
      // exige selección — el operador debe decir a qué cuenta entró el dinero.
      if (CUENTA_TIPO_POR_FORMA[pago.forma_pago] && !pago.cuenta_destino_id) return false;
      // Validar pago directo del cliente a Entretur (flujo paralelo)
      if (pago.pago_directo_check) {
        const monto = Number(pago.pago_directo_monto) || 0;
        if (monto <= 0) return false;
        if (monto > precioTotal + 0.01) return false;
        // Métodos con cuenta destino exigen selección
        if (CUENTA_TIPO_POR_FORMA[pago.pago_directo_metodo] && !pago.pago_directo_cuenta_destino_id) return false;
        // Métodos con referencia (transferencia/tarjeta) la exigen
        if (['transferencia', 'tarjeta'].includes(pago.pago_directo_metodo) && !(pago.pago_directo_referencia || '').trim()) return false;
        // Comprobante OBLIGATORIO salvo efectivo (regla del operador)
        if (pago.pago_directo_metodo !== 'efectivo' && !(pago.pago_directo_comprobante_url || '').trim()) return false;
      }
      // Validar cada pago directo ADICIONAL (extras) con las mismas reglas
      for (const extra of (pago.pagos_directos_extras || [])) {
        if (!extra) continue;
        const m = Number(extra.monto) || 0;
        if (m <= 0) return false;
        if (!extra.metodo_pago) return false;
        if (CUENTA_TIPO_POR_FORMA[extra.metodo_pago] && !extra.cuenta_destino_id) return false;
        if (['transferencia', 'tarjeta'].includes(extra.metodo_pago) && !(extra.referencia || '').trim()) return false;
        if (extra.metodo_pago !== 'efectivo' && !(extra.comprobante_url || '').trim()) return false;
      }
      // Validar comisión anticipada: tope = comisionTotal − descuento sobre comisión
      // (com_ant + desc_com ≤ com_total). Descuento_comercial fue eliminado.
      if (pago.comision_anticipada_check) {
        const descCom = pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0;
        const tope = Math.max(comisionTotal - descCom, 0);
        const monto = Number(pago.comision_anticipada) || 0;
        if (monto <= 0 || monto > tope + 0.01) return false;
      }
      // Validar pago parcial: el operador ingresa el monto BRUTO recibido por
      // la agencia (incluye la comisión que ella retiene). Reglas:
      //   - 0 < mt (< precio si no hay pago directo, sino puede ≤ precio)
      //   - comAnt ≤ mt (la agencia no puede retener más comisión que lo recibido)
      //   - mt + pago_directo ≤ precio (no cobrarle al cliente más que el total)
      // saldo_a_cargo_de obligatorio. Aplica a cualquier método físico.
      if (['transferencia', 'efectivo', 'tarjeta', 'nequi'].includes(pago.forma_pago)
          && pago.transferencia_parcial_check) {
        const mt = Number(pago.monto_transferido) || 0;
        const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
        const pagoDirecto = (pago.pago_directo_check ? (Number(pago.pago_directo_monto) || 0) : 0)
          + (pago.pagos_directos_extras || []).reduce((acc, x) => acc + (Number(x.monto) || 0), 0);
        if (mt <= 0) return false;
        // Si no hay pago directo, mt < precio (invariante original).
        // Si hay pago directo, mt puede ser incluso ≤ precio (mt + directo ≤ precio con saldo muelle ≥ 0).
        if (pagoDirecto === 0 && mt >= precioTotal - 0.01) return false;
        if (comAnt > mt + 0.01) return false;
        if ((mt + pagoDirecto) > precioTotal + 0.01) return false;
        if (!pago.saldo_a_cargo_de) return false;
      }
      // Validar descuento sobre comisión: tope = comisionTotal − comision_anticipada.
      // El backend rechaza com_ant + desc_com > com_total.
      if (pago.descuento_comision_check) {
        const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
        const topeDesc = Math.max(comisionTotal - comAnt, 0);
        const desc = Number(pago.descuento_comision) || 0;
        if (desc <= 0 || desc > topeDesc + 0.01) return false;
      }
      return true;
    }
    return true;
  }, [step, tipo, salidaId, alquilerId, fecha, form, pago, comisionTotal]);

  const handleNext = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const handlePrev = () => setStep(s => Math.max(s - 1, 0));

  const handleCrear = async () => {
    setCreando(true);
    try {
      // Validación de cortesías
      const _cortA = Number(form.cortesias_adultos) || 0;
      const _cortN = Number(form.cortesias_ninos) || 0;
      const _totalCort = _cortA + _cortN;
      if (tipo === 'pasadia' && _totalCort > 0) {
        if (!form.cortesias_autorizada_por_nombre.trim()) {
          toast.error('Indica quién autorizó la cortesía (nombre).');
          setCreando(false);
          return;
        }
        if (!form.cortesias_motivo.trim()) {
          toast.error('Indica el motivo de la cortesía.');
          setCreando(false);
          return;
        }
      }
      const paxTotal = (Number(form.adultos) || 0) + (Number(form.ninos) || 0) + (Number(form.bebes) || 0);
      const body = {
        empresa_id: empresaId,
        tipo_reservacion: tipo,
        nombre_cliente: form.nombre_cliente.trim(),
        numero_whatsapp: form.numero_whatsapp.trim(),
        correo: form.correo.trim() || null,
        adultos: Number(form.adultos) || 1,
        ninos: tipo === 'alquiler' ? 0 : Number(form.ninos) || 0,
        bebes: tipo === 'alquiler' ? 0 : Number(form.bebes) || 0,
        cortesias: tipo === 'alquiler' ? 0 : Math.max(Number(form.cortesias) || 0, _totalCort),
        // Migración 084: cortesías diferenciadas + motivo + autorizador
        cortesias_adultos: tipo === 'alquiler' ? 0 : _cortA,
        cortesias_ninos:   tipo === 'alquiler' ? 0 : _cortN,
        cortesias_motivo:                _totalCort > 0 ? form.cortesias_motivo.trim() : null,
        cortesias_autorizada_por_nombre: _totalCort > 0 ? form.cortesias_autorizada_por_nombre.trim() : null,
        agencia_id: form.agencia_id ? parseInt(form.agencia_id) : null,
        notas: form.notas || null,
        requerimientos_especiales: form.requerimientos_especiales || null,
        canal_reserva: form.canal_reserva,
        // En modo mixto, la forma_pago_agencia se fuerza a 'mixto' y se envía
        // el detalle de cada línea. El backend genera N vouchers (uno por línea).
        forma_pago_agencia: pago.mixto_check ? 'mixto' : pago.forma_pago,
        referencia_pago: pago.mixto_check ? null : (pago.referencia_pago || null),
        comprobante_url: pago.mixto_check ? null : (pago.comprobante_url || null),
        lineas_pago: pago.mixto_check
          ? pago.lineas_mixto.map(ln => ({
              metodo_pago: ln.metodo_pago,
              monto: Number(ln.monto),
              referencia: (ln.referencia || '').trim() || null,
            }))
          : null,
        // descuento_comercial fue removido del wizard: el descuento solo sale
        // de la comisión (sea de agencia tercera o de Entretur como agencia 13).
        descuento_comercial_por_persona: 0,
        descuento_comercial: 0,
        descuento_autorizado_por_id: null,
        descuento_motivo: null,
        // Comisión anticipada: monto que la agencia cobró directamente al cliente
        // y por lo tanto Entretur NO se lo debe a ella.
        comision_anticipada: pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0,
        // FLUJO DE PAGO — 2 modos coexisten:
        //   (a) LEGACY: transferencia_parcial_check activo → monto_transferido
        //       = lo que la agencia RECIBIÓ del cliente (bruto).
        //   (b) NUEVO (con agencia, sin transferencia_parcial): la agencia NO
        //       recibe físicamente el dinero — todo va por comisión anticipada
        //       (retención) + pagos directos + saldo. Enviamos monto_transferido=0
        //       para que aplicar_financiero calcule bien el saldo restante.
        // NOTA: aquí no tenemos disponible `esCortesia100` (vive en StepPago).
        // Lo detectamos con forma_pago === 'cortesia' (que es el marker canónico).
        monto_transferido: (() => {
          const hayAgencia = !!form.agencia_id;
          const esCortesia = pago.forma_pago === 'cortesia';
          if (pago.mixto_check) {
            return pago.transferencia_parcial_check
              ? pago.lineas_mixto.reduce((acc, ln) => acc + (Number(ln.monto) || 0), 0)
              : null;
          }
          if (pago.transferencia_parcial_check
              && ['transferencia', 'efectivo', 'tarjeta', 'nequi'].includes(pago.forma_pago)) {
            return Math.max(
              (Number(pago.monto_transferido) || 0)
                - (pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0),
              0
            );
          }
          // FIX 2: modo nuevo con agencia → agencia no recibe nada físicamente
          return (hayAgencia && !esCortesia) ? 0 : null;
        })(),
        // FIX 1: saldo_a_cargo_de siempre que haya agencia — el valor lo capturó
        // el operador en el Canal 3 (muelle | agencia). Antes solo se enviaba en
        // transferencia_parcial, causando que el saldo quedara "en el aire".
        // Cuando hay saldo_dividido, el backend lo sobreescribe correctamente.
        saldo_a_cargo_de: (() => {
          const hayAgencia = !!form.agencia_id;
          const esCortesia = pago.forma_pago === 'cortesia';
          return (hayAgencia && !esCortesia)
            ? (pago.saldo_a_cargo_de || 'muelle')
            : null;
        })(),
        // Descuento que la agencia regala al turista de SU comisión
        // (distinto del descuento_comercial que asume Entretur).
        descuento_comision: pago.descuento_comision_check
          ? (Number(pago.descuento_comision) || 0)
          : 0,
        // Cuenta destino — aplica a todo método con `requiereCuentaDestino`
        // (Nequi, Wompi, Transferencia, Bold). Para los demás se envía null.
        cuenta_destino_id: CUENTA_TIPO_POR_FORMA[pago.forma_pago] && pago.cuenta_destino_id
          ? parseInt(pago.cuenta_destino_id)
          : null,
        // Pago directo del cliente a Entretur (flujo paralelo). Backend crea
        // PagoReservacion(origen='pago_directo_cliente') y ajusta saldo.
        pago_directo_entretur: pago.pago_directo_check && Number(pago.pago_directo_monto) > 0 ? {
          monto: Number(pago.pago_directo_monto),
          metodo_pago: pago.pago_directo_metodo,
          cuenta_destino_id: pago.pago_directo_cuenta_destino_id ? parseInt(pago.pago_directo_cuenta_destino_id) : null,
          referencia: (pago.pago_directo_referencia || '').trim() || null,
          comprobante_url: pago.pago_directo_comprobante_url || null,
        } : null,
        // Pagos directos ADICIONALES (mixto con agencia). Se envían solo los
        // válidos (monto > 0 y método presente). El backend los procesa como
        // N PagoReservacion + N VoucherPago independientes.
        pagos_directos_entretur: (pago.pagos_directos_extras || [])
          .filter(p => p && Number(p.monto) > 0 && p.metodo_pago)
          .map(p => ({
            monto: Number(p.monto),
            metodo_pago: p.metodo_pago,
            cuenta_destino_id: p.cuenta_destino_id ? parseInt(p.cuenta_destino_id) : null,
            referencia: (p.referencia || '').trim() || null,
            comprobante_url: p.comprobante_url || null,
          })),
        // Saldo a favor de la agencia aplicado en el mismo acto de crear.
        // Se manda el tope: el backend aplica lo que alcance contra el saldo
        // que quede al final, después de pagos directos y saldo dividido.
        aplicar_saldo_a_favor: (usarSaldoFavor && saldoFavorAgencia > 0)
          ? saldoFavorAgencia : null,
        // Saldo dividido — solo se envía si el operador activó el modo.
        saldo_dividido: pago.saldo_dividido_check
          && (Number(pago.saldo_dividido_cxc) > 0 || Number(pago.saldo_dividido_muelle) > 0)
          ? {
              cxc_agencia_monto: Number(pago.saldo_dividido_cxc) || 0,
              saldo_muelle_monto: Number(pago.saldo_dividido_muelle) || 0,
            }
          : null,
      };
      if (tipo === 'pasadia') {
        body.salida_id = parseInt(salidaId);
      } else {
        body.alquiler_id = parseInt(alquilerId);
        body.fecha_alquiler = fecha;
      }
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        body.descuento_solicitado_por_id = u.id || u.user_id || null;
        // Auditoría: quién está creando esta reserva desde el wizard.
        // Lo usa /reservaciones para registrar "reserva_creada_admin" con autor.
        body.usuario_id = u.id || u.user_id || null;
        const partes = [u.name, u.lastname].filter(Boolean);
        body.usuario_nombre = partes.join(' ').trim() || u.email || null;
      } catch {}

      const r = await fetch(`${API_BASE}/turismo/reservaciones`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      // Leer como texto primero: si el backend devuelve un 500 con body de texto
      // plano (ej. traceback no manejado), r.json() lanzaba y caíamos al catch
      // genérico "Error de red", ocultando la causa real.
      const raw = await r.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = { detail: raw }; }
      if (!r.ok || !data.success) {
        const msg = data.detail || data.message
          || (r.status >= 500 ? `Error del servidor (${r.status})` : 'Error creando reserva');
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
        setCreando(false);
        return;
      }
      setResultado(data);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.message ? `Error de red: ${e.message}` : 'Error de red');
    }
    setCreando(false);
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      variant="wide"
      title={resultado ? '¡Reserva creada!' : 'Nueva reserva'}
      subtitle={resultado ? resultado.codigo_reserva : `Paso ${step + 1} de ${STEPS.length}`}
      icon={<AddIcon />}
      footer={resultado ? (
        <Button variant="contained" onClick={onClose} disableElevation
          sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
          Listo
        </Button>
      ) : (
        <>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
          {step > 0 && (
            <Button onClick={handlePrev} sx={{ textTransform: 'none' }}>Atrás</Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button variant="contained" disabled={!canNext} onClick={handleNext} disableElevation
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              Siguiente
            </Button>
          ) : (
            <Button variant="contained" disabled={!canNext || creando} onClick={handleCrear} disableElevation
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              {creando ? 'Creando…' : `Crear reserva — ${formatPrecio(Math.max(precioTotal - (pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0), 0))}`}
            </Button>
          )}
        </>
      )}
    >
      {resultado ? (
        <ResultPanel resultado={resultado} />
      ) : (
        <>
          <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
            {STEPS.map(s => (
              <Step key={s}><StepLabel>{s}</StepLabel></Step>
            ))}
          </Stepper>

          {/* Banner promo estacional aplicada — visible en todos los steps cuando
              la salida elegida cae dentro de un rango promocional activo. */}
          {salidaSel && salidaSel.promo_aplicada && (
            <Box sx={{
              p: 1.25, mb: 1.5, borderRadius: 2,
              bgcolor: '#fef3c7', border: '1px solid #fde68a',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <Typography sx={{ fontSize: 18 }}>🏷️</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#92400e' }}>
                  Promo estacional aplicada: {salidaSel.promo_aplicada.nombre}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#78716c' }}>
                  Neto promo adulto: <b>${Number(salidaSel.promo_aplicada.precio_neto_adulto).toLocaleString('es-CO')}</b>
                  {' '}vs base <b>${Number(salidaSel.precio_neto_adulto ?? 0).toLocaleString('es-CO')}</b>
                  {salidaSel.promo_aplicada.precio_neto_nino != null && (
                    <> · niño <b>${Number(salidaSel.promo_aplicada.precio_neto_nino).toLocaleString('es-CO')}</b></>
                  )}
                  {' '}· hasta {salidaSel.promo_aplicada.fecha_fin}
                </Typography>
              </Box>
            </Box>
          )}

          {step === 0 && (
            <StepSalida
              tipo={tipo} setTipo={setTipo}
              fecha={fecha} setFecha={setFecha}
              salidaId={salidaId} setSalidaId={setSalidaId}
              salidasDelDia={salidasDelDia} cargando={cargando} diaCerrado={diaCerrado}
              alquilerId={alquilerId} setAlquilerId={setAlquilerId}
              alquileres={alquileres}
            />
          )}

          {step === 1 && (
            <StepPasajeros
              tipo={tipo} form={form} setForm={setForm} agencias={agencias}
            />
          )}

          {step === 2 && (
            <StepPago
              pago={pago} setPago={setPago}
              userRole={userRole} adminsEmpresa={adminsEmpresa}
              paxTotal={(Number(form.adultos) || 0) + (Number(form.ninos) || 0) + (Number(form.bebes) || 0)}
              comisionTotal={comisionTotal}
              agencia={agencias.find(a => a.id === parseInt(form.agencia_id))}
              precioTotal={precioTotal}
              cuentasDestino={cuentasDestino}
              esCortesia100={esCortesia100}
              cortesiasMotivo={form.cortesias_motivo}
              cortesiasAutorizadaPor={form.cortesias_autorizada_por_nombre}
              saldoFavorAgencia={saldoFavorAgencia}
              usarSaldoFavor={usarSaldoFavor}
              setUsarSaldoFavor={setUsarSaldoFavor}
            />
          )}

          {step === 3 && (
            <StepConfirmar
              tipo={tipo} salidaSel={salidaSel} alquilerSel={alquilerSel}
              fecha={fecha} form={form} pago={pago} precioTotal={precioTotal}
              comisionTotal={comisionTotal}
              agencia={agencias.find(a => a.id === parseInt(form.agencia_id))}
              cuentasDestino={cuentasDestino}
            />
          )}
        </>
      )}
      <ToastContainer />
    </SlideOver>
  );
}

// ----------------------------------------------------------------------------
// Pasos
// ----------------------------------------------------------------------------

// El backend serializa con datetime.utcnow().isoformat(), o sea sin sufijo de
// zona. Sin marcarlo como UTC, `new Date()` lo lee como hora local y la firma
// del cierre se muestra corrida — el mismo defecto que ya apareció en el
// desglose de cobros del cierre de muelle.
function fmtFechaHoraBogota(iso) {
  if (!iso) return '';
  const t = String(iso);
  const utc = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(t) ? t : `${t}Z`;
  try {
    return new Date(utc).toLocaleString('es-CO', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      hour12: true, timeZone: 'America/Bogota',
    });
  } catch { return ''; }
}

function StepSalida({ tipo, setTipo, fecha, setFecha, salidaId, setSalidaId, salidasDelDia, cargando, alquilerId, setAlquilerId, alquileres, diaCerrado }) {
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[
          { v: 'pasadia',  label: 'Pasadía / Tour' },
          { v: 'alquiler', label: 'Alquiler de embarcación' },
        ].map(t => (
          <Chip key={t.v}
            label={t.label}
            onClick={() => setTipo(t.v)}
            sx={{
              fontWeight: 700, fontSize: 12, height: 36, px: 1.5,
              bgcolor: tipo === t.v ? '#0d9488' : '#f1f5f9',
              color: tipo === t.v ? '#fff' : '#475569',
              '&:hover': { bgcolor: tipo === t.v ? '#0f766e' : '#e2e8f0' },
            }} />
        ))}
      </Box>

      <TextField size="small" type="date" label="Fecha de salida"
        value={fecha} onChange={e => setFecha(e.target.value)}
        InputLabelProps={{ shrink: true }} />

      {/* Día cerrado: no se listan salidas. Mostrarlas sería ofrecer algo que
          el sistema va a rechazar al final — el vendedor llena todo el
          formulario para nada, y peor, frente al cliente. */}
      {diaCerrado ? (
        <Alert severity="error" sx={{ fontSize: 12.5 }}>
          <Box sx={{ fontWeight: 800, mb: 0.5 }}>
            Este día ya está cerrado — no admite ventas nuevas
          </Box>
          El cierre de muelle <strong>{diaCerrado.codigo}</strong> fue firmado
          {diaCerrado.firmado_por ? <> por <strong>{diaCerrado.firmado_por}</strong></> : null}
          {diaCerrado.firmado_at ? ` el ${fmtFechaHoraBogota(diaCerrado.firmado_at)}` : ''}.
          <Box sx={{ mt: 0.75, color: '#7f1d1d' }}>
            Elige otra fecha. Si esta venta tiene que entrar hoy sí o sí, el
            coordinador debe reabrir el cierre primero.
          </Box>
        </Alert>
      ) : tipo === 'pasadia' ? (
        <>
          {cargando ? (
            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : salidasDelDia.length === 0 ? (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              No hay salidas con cupo disponible para esta fecha.
            </Alert>
          ) : (
            <Stack spacing={0.75}>
              {salidasDelDia.map(s => {
                const cap = s.capacidad_total || s.capacidad_override || 0;
                const libres = cap - (s.asientos_reservados || 0);
                const sel = parseInt(salidaId) === s.id;
                return (
                  <Box key={s.id} onClick={() => setSalidaId(String(s.id))}
                    sx={{
                      p: 1.25, borderRadius: 2, cursor: 'pointer',
                      border: sel ? '2px solid #0d9488' : '1px solid #e2e8f0',
                      bgcolor: sel ? '#f0fdfa' : '#fff',
                      '&:hover': { borderColor: '#0d9488' },
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        {s.paquete_nombre || s.paquete?.nombre}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: libres > 5 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                        {libres} cupos
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                      {s.hora_salida?.slice(0, 5)} · {formatPrecio(s.precio_adulto_override ?? s.precio_adulto ?? s.paquete?.precio_adulto)} adulto
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </>
      ) : (
        <TextField size="small" select label="Embarcación" value={alquilerId}
          onChange={e => setAlquilerId(e.target.value)}>
          <MenuItem value="">— Selecciona —</MenuItem>
          {alquileres.map(a => (
            <MenuItem key={a.id} value={a.id}>{a.nombre} · {formatPrecio(a.precio)}</MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

function StepPasajeros({ tipo, form, setForm, agencias }) {
  const Counter = ({ label, field, min = 0 }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
      </Box>
      <IconButton size="small" onClick={() => setForm(f => ({ ...f, [field]: Math.max(min, (Number(f[field]) || 0) - 1) }))}
        sx={{ bgcolor: '#f1f5f9' }}>
        <RemoveIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography sx={{ fontSize: 14, fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{form[field]}</Typography>
      <IconButton size="small" onClick={() => setForm(f => ({ ...f, [field]: (Number(f[field]) || 0) + 1 }))}
        sx={{ bgcolor: '#f1f5f9' }}>
        <AddIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  return (
    <Stack spacing={1.5}>
      <TextField size="small" label="Nombre del cliente *" value={form.nombre_cliente}
        onChange={e => setForm({ ...form, nombre_cliente: e.target.value })} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField size="small" label="WhatsApp *" value={form.numero_whatsapp}
          onChange={e => setForm({ ...form, numero_whatsapp: e.target.value })}
          placeholder="+57 300…" />
        <TextField size="small" label="Correo" value={form.correo}
          onChange={e => setForm({ ...form, correo: e.target.value })} />
      </Box>

      <Counter label="Adultos" field="adultos" min={tipo === 'alquiler' ? 1 : 0} />
      {tipo === 'pasadia' && (
        <>
          <Counter label="Niños" field="ninos" />
          <Counter label="Bebés" field="bebes" />

          {/* Cortesías diferenciadas (Mig. 084) */}
          <Box sx={{ p: 1.25, border: '1px dashed #fbbf24', borderRadius: 2, bgcolor: '#fffbeb' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400e', mb: 0.75 }}>
              🎁 Cortesías (opcional · no pagan, no generan comisión)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              <TextField label="Cortesías adultos" type="number" size="small"
                value={form.cortesias_adultos}
                onChange={e => setForm({ ...form,
                  cortesias_adultos: Math.max(0, Math.min(Number(form.adultos) || 0, parseInt(e.target.value) || 0)) })}
                inputProps={{ min: 0, max: Number(form.adultos) || 0 }}
                helperText={`Máx ${Number(form.adultos) || 0}`} />
              <TextField label="Cortesías niños" type="number" size="small"
                value={form.cortesias_ninos}
                onChange={e => setForm({ ...form,
                  cortesias_ninos: Math.max(0, Math.min(Number(form.ninos) || 0, parseInt(e.target.value) || 0)) })}
                inputProps={{ min: 0, max: Number(form.ninos) || 0 }}
                helperText={`Máx ${Number(form.ninos) || 0}`} />
            </Box>
            {((Number(form.cortesias_adultos) || 0) + (Number(form.cortesias_ninos) || 0)) > 0 && (
              <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                <TextField label="Autorizada por (nombre)" size="small" fullWidth required
                  value={form.cortesias_autorizada_por_nombre}
                  onChange={e => setForm({ ...form, cortesias_autorizada_por_nombre: e.target.value })}
                  placeholder="Ej. María del Pilar Salgado" />
                <TextField label="Motivo" size="small" fullWidth required multiline minRows={2}
                  value={form.cortesias_motivo}
                  onChange={e => setForm({ ...form, cortesias_motivo: e.target.value })}
                  placeholder="Ej. Familiar del vendedor · Promoción radio · Influencer cliente" />
              </Box>
            )}
          </Box>
        </>
      )}

      <Divider sx={{ my: 0.5 }} />

      <TextField size="small" select label="Agencia (opcional)" value={form.agencia_id}
        onChange={e => setForm({ ...form, agencia_id: e.target.value })}>
        <MenuItem value="">— Venta directa —</MenuItem>
        {agencias.map(a => (
          <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
        ))}
      </TextField>

      <TextField size="small" multiline minRows={2} label="Notas" value={form.notas}
        onChange={e => setForm({ ...form, notas: e.target.value })} />

      <TextField size="small" multiline minRows={2} label="Requerimientos especiales"
        value={form.requerimientos_especiales}
        onChange={e => setForm({ ...form, requerimientos_especiales: e.target.value })}
        placeholder="Alergias, movilidad reducida, etc." />
    </Stack>
  );
}

// Infiere `forma_pago_agencia` a partir de la distribución del pago.
// Cuando el operador captura la distribución (comisión anticipada / pago directo /
// saldo dividido / saldo a cargo de) el valor comercial se deduce automáticamente.
// Solo cuando `override_manual_forma_pago=true` el operador puede forzar otro valor.
//
// Reglas:
//   1. Cortesía → 'cortesia'
//   2. Múltiples canales activos (comisión + directo + saldo dividido) → 'mixto'
//   3. Múltiples pagos directos (2+) → 'mixto'
//   4. Saldo dividido (CxC + muelle) → 'mixto'
//   5. Solo comisión anticipada + saldo a cargo de agencia → 'credito'
//   6. Solo comisión anticipada + saldo a cargo de muelle → 'muelle'
//   7. Solo pago directo → método del pago directo (transferencia/nequi/...)
//   8. Sin pagos → según saldo_a_cargo_de ('credito' o 'muelle')
function inferirFormaPagoAgencia(pago) {
  if (pago.forma_pago === 'cortesia') return 'cortesia';

  const tieneCA = pago.comision_anticipada_check && Number(pago.comision_anticipada) > 0;
  const hayPDPrincipal = pago.pago_directo_check && Number(pago.pago_directo_monto) > 0;
  const extras = (pago.pagos_directos_extras || []).filter(x => Number(x.monto) > 0);
  const tienePD = hayPDPrincipal || extras.length > 0;
  const totalPDs = (hayPDPrincipal ? 1 : 0) + extras.length;
  const tieneSaldoDiv = pago.saldo_dividido_check
    && (Number(pago.saldo_dividido_cxc) > 0 || Number(pago.saldo_dividido_muelle) > 0);

  // 2, 3, 4 · casos mixto
  const canalesActivos = [tieneCA, tienePD, tieneSaldoDiv].filter(Boolean).length;
  if (canalesActivos >= 2) return 'mixto';
  if (totalPDs >= 2) return 'mixto';
  if (tieneSaldoDiv) return 'mixto';

  // 7 · solo pago directo
  if (tienePD && !tieneCA) {
    return pago.pago_directo_metodo === 'bold_link' ? 'transferencia'
      : (pago.pago_directo_metodo || 'transferencia');
  }

  // 5, 6 · solo comisión anticipada + saldo
  if (tieneCA && !tienePD) {
    return pago.saldo_a_cargo_de === 'agencia' ? 'credito' : 'muelle';
  }

  // 8 · nada capturado, va según saldo_a_cargo_de
  return pago.saldo_a_cargo_de === 'agencia' ? 'credito' : 'muelle';
}


// Uploader compacto reutilizable — sube archivo a /turismo/soportes/upload y
// guarda la URL. Se usa para el comprobante obligatorio de pagos directos que
// no son en efectivo. Marcarlo con `obligatorio` sirve para mostrar el warning
// cuando aún no se ha subido nada.
function UploaderInline({ label, valueUrl, onChange, obligatorio = false }) {
  const [subiendo, setSubiendo] = React.useState(false);
  const [nombre, setNombre] = React.useState('');
  const handleFile = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, { method: 'POST', body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || 'Error subiendo comprobante');
      } else {
        const data = await r.json();
        onChange(data.url);
        setNombre(file.name);
      }
    } catch (err) {
      alert('Error subiendo comprobante: ' + err.message);
    }
    setSubiendo(false);
  };
  const cargado = !!valueUrl;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
      p: 0.75, borderRadius: 1,
      bgcolor: cargado ? '#dcfce7' : (obligatorio ? '#fef3c7' : '#f8fafc'),
      border: `1px dashed ${cargado ? '#86efac' : (obligatorio ? '#fcd34d' : '#e2e8f0')}`,
    }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700,
          color: cargado ? '#166534' : (obligatorio ? '#92400e' : '#475569') }}>
        {cargado ? '✓ Comprobante cargado' : (obligatorio ? '⚠ ' + label : label)}
      </Typography>
      {cargado && nombre && (
        <Typography sx={{ fontSize: 10, color: '#64748b', flex: 1 }} noWrap>· {nombre}</Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Button size="small" variant="outlined" component="label" disabled={subiendo}
        sx={{ textTransform: 'none', fontSize: 10.5, py: 0.25, minWidth: 0 }}>
        {subiendo ? '...' : (cargado ? 'Cambiar' : 'Subir archivo')}
        <input type="file" hidden accept="image/*,application/pdf"
          onChange={e => handleFile(e.target.files?.[0])} />
      </Button>
      {cargado && (
        <Button size="small" variant="text"
          onClick={() => { onChange(''); setNombre(''); }}
          sx={{ textTransform: 'none', fontSize: 10.5, color: '#dc2626', py: 0.25, minWidth: 0 }}>
          Quitar
        </Button>
      )}
    </Box>
  );
}


function StepPago({ pago, setPago, userRole, adminsEmpresa, paxTotal, comisionTotal = 0, agencia, precioTotal = 0, cuentasDestino = null, esCortesia100 = false, cortesiasMotivo = '', cortesiasAutorizadaPor = '', saldoFavorAgencia = 0, usarSaldoFavor = false, setUsarSaldoFavor = () => {} }) {
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const [subiendo, setSubiendo] = React.useState(false);
  const [nombreArchivo, setNombreArchivo] = React.useState('');

  // Las formas de pago que requieren soporte (efectivo opcional, las demás recomendado).
  // En modo mixto cada línea tiene su propia referencia/comprobante;
  // el bloque de soporte único no aplica.
  const requiereSoporte = !pago.mixto_check && pago.forma_pago !== 'bold_link';

  // Auto-inferencia de `forma_pago` cuando hay agencia y NO está en modo override
  // manual. La distribución es la fuente de verdad; el selector de "cómo pagó a
  // la agencia" pasa a ser un display auto-calculado (visible en el accordion
  // "Detalles técnicos" al final). Cuando el operador activa override, el
  // selector vuelve a aparecer y este effect deja de tocar `forma_pago`.
  const formaInferida = inferirFormaPagoAgencia(pago);
  useEffect(() => {
    if (!agencia) return;
    if (pago.override_manual_forma_pago) return;
    if (esCortesia100) return;
    if (pago.forma_pago === formaInferida) return;
    setPago(p => ({ ...p, forma_pago: formaInferida }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencia, formaInferida, pago.override_manual_forma_pago, esCortesia100]);

  const handleUpload = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, { method: 'POST', body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || 'Error subiendo comprobante');
      } else {
        const data = await r.json();
        setPago(p => ({ ...p, comprobante_url: data.url }));
        setNombreArchivo(file.name);
      }
    } catch (err) {
      alert('Error subiendo comprobante: ' + err.message);
    }
    setSubiendo(false);
  };

  return (
    <Stack spacing={1.5}>
      {/* La agencia ya tiene plata entregada de más, esperando. Se avisa acá
          —donde se decide cómo se paga— porque si hay que acordarse de ir a
          buscarla, no se usa. Aplicarla es un botón en la reserva ya creada:
          el saldo tiene que existir antes de poder bajarlo. */}
      {saldoFavorAgencia > 0 && !esCortesia100 && (
        <Alert severity="success" icon={false} sx={{
          bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid #86efac',
          fontSize: 12, py: 0.5,
        }}>
          <Box>
            💚 <b>{agencia?.nombre || 'Esta agencia'}</b> tiene{' '}
            <b>{formatPrecio(saldoFavorAgencia)}</b> a favor de un pago anterior.
          </Box>
          <FormControlLabel
            control={<Checkbox size="small" checked={!!usarSaldoFavor}
                               onChange={(e) => setUsarSaldoFavor(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              Usarlo en esta reserva
            </Typography>}
            sx={{ mt: -0.5, mb: -0.5 }}
          />
          {usarSaldoFavor && (
            <Typography sx={{ fontSize: 11 }}>
              Se aplicará lo que alcance contra el saldo que quede al final.
            </Typography>
          )}
        </Alert>
      )}
      {/* Banner cuando es reserva 100% cortesía: no se cobra nada y la
          forma de pago queda bloqueada en 'cortesia'. */}
      {esCortesia100 && (
        <Alert severity="info" icon={false} sx={{
          bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b',
          fontSize: 12, py: 0.5,
        }}>
          <b>🎁 Reserva 100% cortesía</b> — no se cobra nada al cliente ni se genera comisión a la agencia.
          {cortesiasAutorizadaPor && <><br/>Autorizada por: <b>{cortesiasAutorizadaPor}</b></>}
          {cortesiasMotivo && <><br/>Motivo: <i>{cortesiasMotivo}</i></>}
        </Alert>
      )}

      {/* Toggle Pago mixto — solo aparece en VENTA DIRECTA (sin agencia).
          Cuando hay agencia, el modelo es distinto: la agencia recibe el pago
          en UN solo método y lo descompone en comisión + crédito (eso lo
          maneja la UI de "Modo Agencia" abajo, no este toggle). */}
      {!agencia && !esCortesia100 && (
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          p: 1.25, borderRadius: 2,
          bgcolor: pago.mixto_check ? '#f0fdfa' : '#f8fafc',
          border: `1px solid ${pago.mixto_check ? '#0d9488' : '#e2e8f0'}`,
        }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
              Pago mixto (varios métodos)
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>
              Cliente paga con varios métodos a la vez (ej: parte transferencia + parte efectivo).
            </Typography>
          </Box>
          <Switch checked={pago.mixto_check}
            onChange={e => setPago({
              ...pago, mixto_check: e.target.checked,
              ...(e.target.checked ? {} : { forma_pago: 'efectivo' }),
            })}
            sx={{ '& .MuiSwitch-thumb': { bgcolor: pago.mixto_check ? '#fff' : undefined },
                  '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#0d9488 !important' } }} />
        </Box>
      )}

      {/* Selector "Forma de pago" — visible siempre sin agencia; con agencia
          se oculta y el valor se auto-infiere de la distribución. Aparece si
          el operador activa el override manual. */}
      {!pago.mixto_check && (!agencia || pago.override_manual_forma_pago || esCortesia100) && (
        <TextField size="small" select
          label={agencia ? '¿Cómo pagó el cliente a la agencia? (manual)' : 'Forma de pago'}
          value={pago.forma_pago}
          disabled={esCortesia100}
          helperText={esCortesia100
            ? 'Bloqueada porque la reserva es 100% cortesía.'
            : (agencia
              ? 'Override manual — la distribución de abajo suele bastar. Solo úsalo si necesitas forzar un valor específico.'
              : '')}
          onChange={e => {
            setPago({ ...pago, forma_pago: e.target.value, referencia_pago: '', comprobante_url: '' });
            setNombreArchivo('');
          }}>
          {FORMA_PAGO
            // En modo cortesía 100% solo mostramos 'cortesia'. En modo normal
            // ocultamos 'cortesia' (no tiene sentido cuando hay monto a pagar).
            .filter(f => esCortesia100 ? f.value === 'cortesia' : f.value !== 'cortesia')
            .filter(f => !f.requiereAgencia || !!agencia)
            .map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
        </TextField>
      )}

      {!pago.mixto_check && (pago.forma_pago === 'transferencia' || pago.forma_pago === 'tarjeta') && (
        <TextField size="small" label={pago.forma_pago === 'tarjeta' ? 'Autorización datáfono *' : 'Referencia transferencia *'}
          value={pago.referencia_pago}
          onChange={e => setPago({ ...pago, referencia_pago: e.target.value })} />
      )}


      {/* Bloque de pago mixto: lista de líneas dinámica + resumen */}
      {pago.mixto_check && (
        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 1, textTransform: 'uppercase' }}>
            Líneas de pago a la agencia
          </Typography>
          <Stack spacing={1}>
            {pago.lineas_mixto.map((ln, idx) => {
              const requiereRef = ln.metodo_pago === 'transferencia' || ln.metodo_pago === 'tarjeta';
              return (
                <Box key={idx} sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700 }}>
                      Línea {idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {pago.lineas_mixto.length > 1 && (
                      <Button size="small" onClick={() => {
                        const nuevas = pago.lineas_mixto.filter((_, i) => i !== idx);
                        setPago({ ...pago, lineas_mixto: nuevas });
                      }}
                        sx={{ textTransform: 'none', fontSize: 11, color: '#dc2626', minWidth: 0 }}>
                        ✕ Quitar
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: requiereRef ? 0.75 : 0 }}>
                    <TextField select size="small" label="Método" value={ln.metodo_pago}
                      onChange={e => {
                        const nuevas = pago.lineas_mixto.map((l, i) =>
                          i === idx ? { ...l, metodo_pago: e.target.value, referencia: '' } : l);
                        setPago({ ...pago, lineas_mixto: nuevas });
                      }}>
                      <MenuItem value="efectivo">💵 Efectivo</MenuItem>
                      <MenuItem value="transferencia">🏦 Transferencia</MenuItem>
                      <MenuItem value="tarjeta">💳 Tarjeta</MenuItem>
                      <MenuItem value="nequi">📱 Nequi</MenuItem>
                    </TextField>
                    <TextField size="small" type="text" label="Monto" value={ln.monto ? String(ln.monto) : ''}
                      onChange={e => {
                        const clean = e.target.value.replace(/[^0-9]/g, '');
                        const nuevas = pago.lineas_mixto.map((l, i) =>
                          i === idx ? { ...l, monto: clean } : l);
                        setPago({ ...pago, lineas_mixto: nuevas });
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                  </Box>
                  {requiereRef && (
                    <TextField fullWidth size="small" label="Referencia *"
                      value={ln.referencia}
                      onChange={e => {
                        const nuevas = pago.lineas_mixto.map((l, i) =>
                          i === idx ? { ...l, referencia: e.target.value } : l);
                        setPago({ ...pago, lineas_mixto: nuevas });
                      }} />
                  )}
                </Box>
              );
            })}
          </Stack>
          {pago.lineas_mixto.length < 4 && (
            <Button size="small" onClick={() => setPago({
              ...pago,
              lineas_mixto: [...pago.lineas_mixto, { metodo_pago: 'efectivo', monto: '', referencia: '' }],
            })}
              sx={{ textTransform: 'none', mt: 1, color: '#0d9488', fontSize: 12 }}>
              + Agregar línea
            </Button>
          )}

          {/* Resumen: suma vs precio total */}
          {(() => {
            const suma = pago.lineas_mixto.reduce((acc, l) => acc + (Number(l.monto) || 0), 0);
            const diff = precioTotal - suma;
            const esParcial = pago.transferencia_parcial_check;
            const cuadra = esParcial ? suma < precioTotal && suma > 0 : Math.abs(diff) < 0.5;
            const bg = cuadra ? '#dcfce7' : (suma > precioTotal ? '#fee2e2' : '#fef3c7');
            const col = cuadra ? '#16a34a' : (suma > precioTotal ? '#dc2626' : '#f59e0b');
            return (
              <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: bg, border: `1px solid ${col}40` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 11, color: '#475569' }}>Precio total reserva</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{formatPrecio(precioTotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 11, color: '#475569' }}>Suma de líneas (recibido por agencia)</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>{formatPrecio(suma)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 11, color: '#475569' }}>
                    {esParcial ? 'Saldo turista en muelle' : 'Diferencia'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: col }}>
                    {cuadra ? (esParcial ? formatPrecio(diff) : '✓ Cuadra')
                      : suma > precioTotal ? `excede en ${formatPrecio(Math.abs(diff))}`
                      : `faltan ${formatPrecio(Math.abs(diff))}`}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Cuenta destino genérica. Aplica a Nequi, Wompi, Transferencia y Bold
          — todas las formas marcadas con `requiereCuentaDestino`. El wizard
          filtra cuentas_destino_pago por tipo según el método elegido. */}
      {!pago.mixto_check && CUENTA_TIPO_POR_FORMA[pago.forma_pago] && (() => {
        const tipo = CUENTA_TIPO_POR_FORMA[pago.forma_pago];
        const tiposOk = tiposDeCuentaPara(pago.forma_pago);
        const cuentasFiltradas = (cuentasDestino || []).filter(c => tiposOk.includes(c.tipo));
        const cfg = CUENTA_LABEL_POR_FORMA[pago.forma_pago] || {
          label: 'Cuenta destino *', placeholder: 'Selecciona la cuenta'
        };
        return (
          <>
            <TextField size="small" select label={cfg.label}
              value={pago.cuenta_destino_id}
              onChange={e => setPago({ ...pago, cuenta_destino_id: e.target.value })}
              disabled={cuentasDestino === null}>
              <MenuItem value="">— {cfg.placeholder} —</MenuItem>
              {cuentasFiltradas.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nombre}{c.titular ? ` · ${c.titular}` : ''}
                </MenuItem>
              ))}
            </TextField>
            {cuentasDestino && cuentasFiltradas.length === 0 && (
              <Alert severity="warning" sx={{ fontSize: 11.5 }}>
                No hay cuentas {tipo} configuradas. Habla con el superadmin para agregar una.
              </Alert>
            )}
          </>
        );
      })()}

      {/* 🧮 DISTRIBUCIÓN DEL PAGO — Vista unificada del reparto del pago del
          cliente en 3 flujos: agencia / directo Entretur / muelle. Los inputs
          escriben en los mismos campos que los toggles individuales de abajo
          (misma data, dos vistas). Sirve para armar el caso complejo en un
          solo lugar visual y verificar que cuadra contra el precio total. */}
      {agencia && !esCortesia100 && !pago.mixto_check && precioTotal > 0 && (() => {
        const setMontoAgencia = (v) => {
          const val = Number(v) || 0;
          const isFull = val >= precioTotal - 0.01;
          setPago(p => ({
            ...p,
            monto_transferido: val,
            transferencia_parcial_check: !isFull && val > 0,
            saldo_a_cargo_de: p.saldo_a_cargo_de || 'muelle',
          }));
        };
        const montoAgencia = pago.transferencia_parcial_check
          ? (Number(pago.monto_transferido) || 0)
          : (['transferencia','efectivo','tarjeta','nequi','credito'].includes(pago.forma_pago) ? precioTotal : 0);
        const montoDirectoBase = pago.pago_directo_check ? (Number(pago.pago_directo_monto) || 0) : 0;
        const montoDirectoExtras = (pago.pagos_directos_extras || [])
          .reduce((acc, x) => acc + (Number(x.monto) || 0), 0);
        const montoDirecto = montoDirectoBase + montoDirectoExtras;
        const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
        const descCom = pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0;
        // Modelo del operador — El descuento y la retención ambos "salen" del
        // pool a distribuir entre canales antes de llegar al Canal 2. El PVP
        // sigue siendo el objetivo total.
        //
        //   Canal 1 (info):   precio nominal completo
        //   Retención agencia: comAnt (sale del pool)
        //   Descuento agencia: descCom (sale del pool — la agencia lo regala)
        //   Canal 2 (pool restante): precio − retención − descuento
        //   Canal 3 (saldo): pool C2 − pago directo capturado
        //
        //   Ejemplo: precio $1.080, retención $90, descuento $90
        //     → C2 pool = 1.080 − 90 − 90 = $900
        //     → si C2 captura $300 directo, C3 = $600 (muelle o CxC)
        //
        // La comisión efectiva (pactada − descuento) y la comisión pendiente
        // (efectiva − retenida) se muestran solo como info en el resumen del C1.
        const comisionEfectiva = Math.max(comisionTotal - descCom, 0);
        const comisionPendiente = Math.max(comisionEfectiva - comAnt, 0);
        const objetivo = precioTotal;
        const saldoRestante = Math.max(precioTotal - comAnt - descCom - montoDirecto, 0);
        const total = comAnt + descCom + montoDirecto + saldoRestante;
        const cuadra = Math.abs(total - precioTotal) < 0.5;
        const excede = (comAnt + descCom + montoDirecto) > precioTotal + 0.5;
        const netoEntretur = saldoRestante;
        const restanteC1 = precioTotal;
        const restanteC2 = Math.max(precioTotal - comAnt - descCom, 0);
        const restanteC3 = saldoRestante;
        // Componente auxiliar del chip
        const ChipRestante = ({ monto, exceso = false }) => (
          <Chip size="small"
            label={`Restante a distribuir: ${formatPrecio(monto)}`}
            sx={{
              height: 20, fontSize: 10, fontWeight: 700,
              bgcolor: exceso ? '#fee2e2' : (monto === 0 ? '#dcfce7' : '#e0f2fe'),
              color: exceso ? '#991b1b' : (monto === 0 ? '#166534' : '#075985'),
              ml: 1,
            }} />
        );
        // Reset — vuelve todos los campos de distribución a su estado inicial
        // vacío. Útil cuando el operador quiere empezar la distribución desde
        // cero sin cerrar el wizard. NO toca la selección de agencia, salida,
        // ni datos del cliente/viajeros — solo el bloque de pago.
        const handleResetDistribucion = () => setPago(p => ({
          ...p,
          // Canal 1 · Comisión
          comision_anticipada_check: false,
          comision_anticipada: 0,
          descuento_comision_check: false,
          descuento_comision: 0,
          // Canal 2 · Directo
          pago_directo_check: false,
          pago_directo_monto: 0,
          pago_directo_metodo: 'transferencia',
          pago_directo_cuenta_destino_id: '',
          pago_directo_referencia: '',
          pago_directo_comprobante_url: '',
          pagos_directos_extras: [],
          // Canal 3 · Saldo
          saldo_a_cargo_de: 'muelle',
          saldo_dividido_check: false,
          saldo_dividido_cxc: 0,
          saldo_dividido_muelle: 0,
          // Legacy campos que la distribución ya no usa pero conviene resetear
          monto_transferido: 0,
          transferencia_parcial_check: false,
          // Fuerza volver a inferencia automática
          override_manual_forma_pago: false,
        }));
        return (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '2px solid #0d9488' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0f766e',
                  textTransform: 'uppercase', letterSpacing: 0.4, flex: 1 }}>
                🧮 Distribución del pago del cliente
              </Typography>
              <Button size="small" variant="outlined" color="warning"
                onClick={handleResetDistribucion}
                sx={{ textTransform: 'none', fontSize: 10.5, py: 0.25,
                      minWidth: 0, borderColor: '#f59e0b', color: '#b45309',
                      '&:hover': { borderColor: '#d97706', bgcolor: '#fef3c7' } }}>
                ↺ Resetear distribución
              </Button>
            </Box>
            <Box sx={{ mb: 1.25, p: 0.75, bgcolor: '#fff', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>
                  Precio total a distribuir (PVP)
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{formatPrecio(objetivo)}</Typography>
              </Box>
            </Box>

            {/* 📍 CANAL 1 · COMISIÓN DE LA AGENCIA
                Solo aquí se maneja lo que la agencia SE QUEDA:
                  - Comisión que retiene (total o parcial)
                  - Descuento que la agencia ofrece sobre su comisión (para
                    cerrar la venta — sale del bolsillo de la agencia)
                El resto del dinero (cómo llega a Entretur, si es CxC, si se
                cobra en muelle) vive en los Canales 2 y 3. */}
            <Box sx={{ p: 1, mb: 1, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #fde68a' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>
                  📍 Canal 1 — Comisión de la agencia · {agencia.nombre}
                </Typography>
                <ChipRestante monto={restanteC1} exceso={comAnt > restanteC1 + 0.5} />
              </Box>
              {/* Resumen automático de la comisión */}
              <Box sx={{ mb: 0.75, p: 0.75, bgcolor: '#fffbeb', borderRadius: 1,
                  border: '1px solid #fde68a' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 0.25 }}>
                  <Typography sx={{ fontSize: 10.5, color: '#78350f' }}>
                    Comisión pactada:
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#78350f',
                      textAlign: 'right' }}>
                    {formatPrecio(comisionTotal)}
                  </Typography>
                  {descCom > 0 && (
                    <>
                      <Typography sx={{ fontSize: 10.5, color: '#78350f' }}>
                        Descuento ofrecido:
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#dc2626',
                          textAlign: 'right' }}>
                        −{formatPrecio(descCom)}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: '#78350f', fontWeight: 700 }}>
                        Comisión efectiva:
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#065f46',
                          textAlign: 'right' }}>
                        {formatPrecio(comisionEfectiva)}
                      </Typography>
                    </>
                  )}
                  <Typography sx={{ fontSize: 10.5, color: '#78350f' }}>
                    Autoretenido:
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#78350f',
                      textAlign: 'right' }}>
                    {formatPrecio(comAnt)}
                  </Typography>
                  {comisionPendiente > 0 && (
                    <>
                      <Typography sx={{ fontSize: 11, color: '#78350f', fontWeight: 800,
                          borderTop: '1px solid #fde68a', pt: 0.25 }}>
                        Pendiente por pagarle:
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#c2410c',
                          borderTop: '1px solid #fde68a', pt: 0.25, textAlign: 'right' }}>
                        {formatPrecio(comisionPendiente)}
                      </Typography>
                      <Typography sx={{ fontSize: 9.5, color: '#78350f',
                          gridColumn: '1 / -1', fontStyle: 'italic' }}>
                        Entretur debe pagar esto a la agencia en la liquidación.
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              {/* Toggle 1 · Descuento sobre comisión (va primero porque
                  define el tope máximo de lo que se puede autoretener) */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                <Switch size="small" checked={pago.descuento_comision_check}
                  disabled={!(comisionTotal > 0)}
                  onChange={e => {
                    const checked = e.target.checked;
                    setPago(p => {
                      // Si se apaga el descuento, no auto-cambiar autoretenido.
                      // Si se enciende con valor previo, mantener autoretenido si
                      // sigue siendo válido (≤ comisión efectiva).
                      const nuevoDesc = checked ? (Number(p.descuento_comision) || 0) : 0;
                      const topeNuevo = Math.max(comisionTotal - nuevoDesc, 0);
                      const anticipadaActual = Number(p.comision_anticipada) || 0;
                      const anticipadaAjustada = p.comision_anticipada_check && anticipadaActual > topeNuevo
                        ? topeNuevo
                        : anticipadaActual;
                      return {
                        ...p,
                        descuento_comision_check: checked,
                        descuento_comision: nuevoDesc,
                        comision_anticipada: anticipadaAjustada,
                      };
                    });
                  }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
                    La agencia ofrece descuento sobre su comisión
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                    Sale del bolsillo de la agencia (para cerrar la venta). Reduce la comisión efectiva.
                  </Typography>
                  {pago.descuento_comision_check && (
                    <TextField size="small" type="text" label="Descuento"
                      value={pago.descuento_comision ? String(pago.descuento_comision) : ''}
                      onChange={e => {
                        const clean = e.target.value.replace(/[^0-9]/g, '');
                        // Al cambiar el descuento, ajustar el autoretenido si
                        // ahora excede el nuevo tope (efectiva).
                        const nuevoDesc = Number(clean) || 0;
                        const topeNuevo = Math.max(comisionTotal - nuevoDesc, 0);
                        setPago(p => {
                          const anticipadaActual = Number(p.comision_anticipada) || 0;
                          const anticipadaAjustada = p.comision_anticipada_check && anticipadaActual > topeNuevo
                            ? topeNuevo
                            : anticipadaActual;
                          return {
                            ...p,
                            descuento_comision: clean,
                            comision_anticipada: anticipadaAjustada,
                          };
                        });
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      helperText={Number(pago.descuento_comision) > comisionTotal
                        ? `⚠ Excede la comisión pactada (${formatPrecio(comisionTotal)})`
                        : ''}
                      error={Number(pago.descuento_comision) > comisionTotal}
                      sx={{ mt: 0.5, width: '100%' }} />
                  )}
                </Box>
              </Box>

              {/* Toggle 2 · Comisión anticipada (retención) — tope = comisión efectiva */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Switch size="small" checked={pago.comision_anticipada_check}
                  disabled={!(comisionEfectiva > 0)}
                  onChange={e => {
                    const checked = e.target.checked;
                    // Al encender, se auto-pobla con la comisión efectiva
                    // (máxima permitida después de aplicar el descuento).
                    setPago(p => ({
                      ...p,
                      comision_anticipada_check: checked,
                      comision_anticipada: checked ? comisionEfectiva : 0,
                    }));
                  }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
                    La agencia retiene su comisión
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                    Puede retener toda su comisión efectiva ({formatPrecio(comisionEfectiva)}) o solo una parte.
                  </Typography>
                  {pago.comision_anticipada_check && (
                    <TextField size="small" type="text" label={`Monto retenido (máx ${formatPrecio(comisionEfectiva)})`}
                      value={pago.comision_anticipada ? String(pago.comision_anticipada) : ''}
                      onChange={e => {
                        const clean = e.target.value.replace(/[^0-9]/g, '');
                        setPago({ ...pago, comision_anticipada: clean });
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      helperText={Number(pago.comision_anticipada) > comisionEfectiva
                        ? `⚠ Excede la comisión efectiva (${formatPrecio(comisionEfectiva)}) — máximo permitido después del descuento`
                        : ''}
                      error={Number(pago.comision_anticipada) > comisionEfectiva}
                      sx={{ mt: 0.5, width: '100%' }} />
                  )}
                </Box>
              </Box>
            </Box>

            {/* 📍 DIRECTO A ENTRETUR */}
            <Box sx={{ p: 1, mb: 1, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #bfdbfe' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Switch size="small" checked={pago.pago_directo_check}
                  onChange={e => setPago({
                    ...pago,
                    pago_directo_check: e.target.checked,
                    ...(e.target.checked ? {} : {
                      pago_directo_monto: 0, pago_directo_referencia: '',
                      pago_directo_comprobante_url: '', pago_directo_cuenta_destino_id: '',
                    }),
                  })} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e40af' }}>
                  📍 Canal 2 — Directo a Entretur (paralelo, no pasa por comisión de agencia)
                </Typography>
                <ChipRestante monto={restanteC2} exceso={montoDirecto > restanteC2 + 0.5} />
              </Box>
              {pago.pago_directo_check && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                  <TextField size="small" type="text" label="Monto *"
                    value={pago.pago_directo_monto ? String(pago.pago_directo_monto) : ''}
                    onChange={e => {
                      const clean = e.target.value.replace(/[^0-9]/g, '');
                      setPago({ ...pago, pago_directo_monto: clean });
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                  <TextField size="small" select label="Método *"
                    value={pago.pago_directo_metodo}
                    onChange={e => setPago({ ...pago, pago_directo_metodo: e.target.value, pago_directo_cuenta_destino_id: '' })}>
                    <MenuItem value="transferencia">🏦 Transferencia</MenuItem>
                    <MenuItem value="nequi">📲 Nequi</MenuItem>
                    <MenuItem value="wompi">📲 Wompi</MenuItem>
                    <MenuItem value="bold_link">💳 Bold</MenuItem>
                    <MenuItem value="efectivo">💵 Efectivo</MenuItem>
                    <MenuItem value="tarjeta">💳 Tarjeta</MenuItem>
                  </TextField>
                  {CUENTA_TIPO_POR_FORMA[pago.pago_directo_metodo] && (() => {
                    const tiposOk = tiposDeCuentaPara(pago.pago_directo_metodo);
                    const cuentasFiltradas = (cuentasDestino || []).filter(c => tiposOk.includes(c.tipo));
                    return (
                      <TextField size="small" select label="Cuenta Entretur *"
                        value={pago.pago_directo_cuenta_destino_id}
                        onChange={e => setPago({ ...pago, pago_directo_cuenta_destino_id: e.target.value })}>
                        <MenuItem value="">— Cuenta —</MenuItem>
                        {cuentasFiltradas.map(c => (
                          <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                        ))}
                      </TextField>
                    );
                  })()}
                  {['transferencia', 'tarjeta'].includes(pago.pago_directo_metodo) && (
                    <TextField size="small" fullWidth
                      label={pago.pago_directo_metodo === 'tarjeta' ? 'Autorización *' : 'Referencia *'}
                      value={pago.pago_directo_referencia}
                      onChange={e => setPago({ ...pago, pago_directo_referencia: e.target.value })}
                      sx={{ gridColumn: '1 / -1' }} />
                  )}
                  {/* Comprobante OBLIGATORIO salvo efectivo */}
                  {pago.pago_directo_metodo !== 'efectivo' && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <UploaderInline
                        label="Comprobante de pago *"
                        obligatorio
                        valueUrl={pago.pago_directo_comprobante_url}
                        onChange={url => setPago({ ...pago, pago_directo_comprobante_url: url })} />
                    </Box>
                  )}
                </Box>
              )}

              {/* Pagos directos ADICIONALES — cuando el cliente pagó a Entretur
                  con varios métodos/cuentas (ej: Bold TMA + Nequi Entretur + Bancolombia Julián).
                  Cada extra genera su propio PagoReservacion + VoucherPago. */}
              {pago.pago_directo_check && (pago.pagos_directos_extras || []).map((extra, idx) => {
                const setExtra = (patch) => {
                  const nuevos = pago.pagos_directos_extras.map((x, i) => i === idx ? { ...x, ...patch } : x);
                  setPago({ ...pago, pagos_directos_extras: nuevos });
                };
                const quitarExtra = () => {
                  const nuevos = pago.pagos_directos_extras.filter((_, i) => i !== idx);
                  setPago({ ...pago, pagos_directos_extras: nuevos });
                };
                const requiereRef = ['transferencia', 'tarjeta'].includes(extra.metodo_pago);
                const tipoCta = CUENTA_TIPO_POR_FORMA[extra.metodo_pago];
                const tiposOkExtra = tiposDeCuentaPara(extra.metodo_pago);
                const ctasFiltradas = tipoCta ? (cuentasDestino || []).filter(c => tiposOkExtra.includes(c.tipo)) : [];
                return (
                  <Box key={idx} sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: '#eff6ff',
                      border: '1px dashed #93c5fd' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#1e40af', flex: 1 }}>
                        Pago directo adicional #{idx + 2}
                      </Typography>
                      <Button size="small" onClick={quitarExtra}
                        sx={{ textTransform: 'none', fontSize: 11, color: '#dc2626', minWidth: 0 }}>
                        ✕ Quitar
                      </Button>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                      <TextField size="small" type="text" label="Monto *"
                        value={extra.monto ? String(extra.monto) : ''}
                        onChange={e => setExtra({ monto: e.target.value.replace(/[^0-9]/g, '') })}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                      <TextField size="small" select label="Método *"
                        value={extra.metodo_pago}
                        onChange={e => setExtra({ metodo_pago: e.target.value, cuenta_destino_id: '' })}>
                        <MenuItem value="transferencia">🏦 Transferencia</MenuItem>
                        <MenuItem value="nequi">📲 Nequi</MenuItem>
                        <MenuItem value="wompi">📲 Wompi</MenuItem>
                        <MenuItem value="bold_link">💳 Bold</MenuItem>
                        <MenuItem value="efectivo">💵 Efectivo</MenuItem>
                        <MenuItem value="tarjeta">💳 Tarjeta</MenuItem>
                      </TextField>
                      {tipoCta && (
                        <TextField size="small" select label="Cuenta Entretur *"
                          value={extra.cuenta_destino_id || ''}
                          onChange={e => setExtra({ cuenta_destino_id: e.target.value })}>
                          <MenuItem value="">— Cuenta —</MenuItem>
                          {ctasFiltradas.map(c => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
                        </TextField>
                      )}
                      {requiereRef && (
                        <TextField size="small" fullWidth
                          label={extra.metodo_pago === 'tarjeta' ? 'Autorización *' : 'Referencia *'}
                          value={extra.referencia || ''}
                          onChange={e => setExtra({ referencia: e.target.value })}
                          sx={{ gridColumn: '1 / -1' }} />
                      )}
                      {/* Comprobante OBLIGATORIO salvo efectivo */}
                      {extra.metodo_pago !== 'efectivo' && (
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <UploaderInline
                            label="Comprobante de pago *"
                            obligatorio
                            valueUrl={extra.comprobante_url}
                            onChange={url => setExtra({ comprobante_url: url })} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {/* Botón "+ Añadir otro pago directo" — solo cuando el check
                  principal está activo (ya hay al menos 1 pago directo) */}
              {pago.pago_directo_check && (
                <Button size="small" onClick={() => setPago({
                  ...pago,
                  pagos_directos_extras: [
                    ...(pago.pagos_directos_extras || []),
                    { monto: '', metodo_pago: 'transferencia',
                      cuenta_destino_id: '', referencia: '', comprobante_url: '' },
                  ],
                })}
                  sx={{ mt: 0.75, textTransform: 'none', fontSize: 11, fontWeight: 700,
                        color: '#1e40af', borderColor: '#93c5fd' }}
                  variant="outlined">
                  + Añadir otro pago directo (a distinta cuenta o método)
                </Button>
              )}
            </Box>

            {/* 📍 EN MUELLE (auto-calculado) */}
            {saldoRestante > 0 && (
              <Box sx={{ p: 1, mb: 1, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #99f6e4' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0f766e' }}>
                      📍 Canal 3 — Saldo restante ({pago.saldo_a_cargo_de === 'agencia' ? 'contra cupo de agencia' : 'muelle al embarcar'})
                    </Typography>
                    <ChipRestante monto={restanteC3} />
                    <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                      = Precio total − A la agencia − Directo a Entretur
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f766e' }}>
                    {formatPrecio(saldoRestante)}
                  </Typography>
                </Box>
                <TextField size="small" select label="Se cobra"
                  value={pago.saldo_dividido_check ? 'dividido' : (pago.saldo_a_cargo_de || 'muelle')}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === 'dividido') {
                      // Al activar la división, pre-poblar mitad y mitad como
                      // sugerencia. El operador ajusta los montos.
                      setPago({
                        ...pago,
                        saldo_dividido_check: true,
                        saldo_dividido_cxc: Math.floor(saldoRestante / 2),
                        saldo_dividido_muelle: saldoRestante - Math.floor(saldoRestante / 2),
                      });
                    } else {
                      setPago({
                        ...pago,
                        saldo_dividido_check: false,
                        saldo_a_cargo_de: v,
                      });
                    }
                  }}
                  sx={{ mt: 0.75, width: '100%' }}>
                  <MenuItem value="muelle">🏖️ Turista paga en muelle al embarcar</MenuItem>
                  <MenuItem value="agencia">📒 Contra cupo de crédito de {agencia.nombre}</MenuItem>
                  <MenuItem value="dividido">🪓 Dividir: parte CxC + parte muelle</MenuItem>
                </TextField>

                {/* Inputs de división (solo cuando dividido) */}
                {pago.saldo_dividido_check && (() => {
                  const cxc = Number(pago.saldo_dividido_cxc) || 0;
                  const mue = Number(pago.saldo_dividido_muelle) || 0;
                  const sumaDiv = cxc + mue;
                  const cuadraDiv = Math.abs(sumaDiv - saldoRestante) < 0.5;
                  return (
                    <Box sx={{ mt: 1, p: 1, borderRadius: 1.5,
                        bgcolor: cuadraDiv ? '#f0fdf4' : '#fef3c7',
                        border: `1px dashed ${cuadraDiv ? '#86efac' : '#fcd34d'}` }}>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#374151', mb: 0.75 }}>
                        División del saldo restante ({formatPrecio(saldoRestante)})
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <TextField size="small" type="text"
                          label={`CxC · ${agencia.nombre}`}
                          value={pago.saldo_dividido_cxc ? String(pago.saldo_dividido_cxc) : ''}
                          onChange={e => setPago({
                            ...pago,
                            saldo_dividido_cxc: e.target.value.replace(/[^0-9]/g, ''),
                          })}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                        <TextField size="small" type="text"
                          label="Cobro en muelle"
                          value={pago.saldo_dividido_muelle ? String(pago.saldo_dividido_muelle) : ''}
                          onChange={e => setPago({
                            ...pago,
                            saldo_dividido_muelle: e.target.value.replace(/[^0-9]/g, ''),
                          })}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                      </Box>
                      <Typography sx={{ fontSize: 10.5, mt: 0.5, fontWeight: 700,
                          color: cuadraDiv ? '#166534' : '#92400e' }}>
                        {cuadraDiv
                          ? `✓ Suma cuadra: ${formatPrecio(sumaDiv)}`
                          : `⚠ Suma ${formatPrecio(sumaDiv)} · Debe ser ${formatPrecio(saldoRestante)}`}
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {/* Balance total */}
            <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: cuadra ? '#dcfce7' : (excede ? '#fee2e2' : '#fef3c7') }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: cuadra ? '#166534' : (excede ? '#991b1b' : '#854d0e') }}>
                  {cuadra ? '✓ Distribución cuadra con el precio' : excede ? '⚠️ La suma excede el precio total' : '⚠️ Falta distribuir'}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                  {formatPrecio(total)} / {formatPrecio(objetivo)}
                </Typography>
              </Box>
            </Box>

            {/* Accordion "Detalles técnicos" — muestra la forma_pago inferida
                y permite override manual si el operador necesita forzarla. */}
            {!esCortesia100 && (
              <Box sx={{ mt: 1, p: 0.75, borderRadius: 1,
                  bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700 }}>
                    ▸ Detalles técnicos ·
                  </Typography>
                  <Chip size="small"
                    label={pago.override_manual_forma_pago
                      ? `Forma pago agencia: ${pago.forma_pago} (manual)`
                      : `Forma pago agencia (auto): ${inferirFormaPagoAgencia(pago)}`}
                    sx={{ fontSize: 10, height: 20, fontWeight: 700,
                          bgcolor: pago.override_manual_forma_pago ? '#fef3c7' : '#e0f2fe',
                          color: pago.override_manual_forma_pago ? '#92400e' : '#075985' }} />
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" variant="text"
                    onClick={() => setPago(p => ({
                      ...p, override_manual_forma_pago: !p.override_manual_forma_pago,
                    }))}
                    sx={{ textTransform: 'none', fontSize: 10.5, minWidth: 0, py: 0.25 }}>
                    {pago.override_manual_forma_pago
                      ? '↩ Volver a auto'
                      : '✏ Ajustar manualmente'}
                  </Button>
                </Box>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.25 }}>
                  {pago.override_manual_forma_pago
                    ? 'Modo manual: el selector "¿Cómo pagó a la agencia?" aparece arriba.'
                    : 'El valor se calcula automáticamente según la distribución que llenaste arriba.'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })()}

      {/* Pago parcial (legacy) — REEMPLAZADO por la caja "🧮 Distribución del
          pago" que está arriba. Se oculta con `false &&` para no duplicar UI y
          eliminar el ruido reportado por el operador. El código queda por si
          hay que revertir. */}
      {false && agencia && !pago.mixto_check && ['transferencia', 'efectivo', 'tarjeta', 'nequi'].includes(pago.forma_pago) && (() => {
        const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
        const mt = Number(pago.monto_transferido) || 0;  // BRUTO: lo que la agencia recibió del cliente
        const topeAbono = Math.max(precioTotal - 1, 1);  // estrictamente menor al precio
        const saldoMuelle = Math.max(precioTotal - mt, 0);  // resto que paga el turista en muelle
        const excedido = pago.transferencia_parcial_check && (mt > precioTotal + 0.01);
        const comExcedeMt = pago.transferencia_parcial_check && (comAnt > mt + 0.01);
        const muyAlto   = pago.transferencia_parcial_check && (mt >= precioTotal - 0.01);
        const labelMetodo = ({
          transferencia: 'transferencia',
          efectivo: 'efectivo',
          tarjeta: 'tarjeta',
          nequi: 'Nequi',
        })[pago.forma_pago] || 'pago';
        return (
          <Box sx={{ p: 1.25, border: '1px solid #fde68a', borderRadius: 2, bgcolor: '#fffbeb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch size="small" checked={pago.transferencia_parcial_check}
                onChange={e => setPago({
                  ...pago,
                  transferencia_parcial_check: e.target.checked,
                  monto_transferido: e.target.checked ? Math.max(precioTotal - comAnt - 1, 0) : 0,
                  saldo_a_cargo_de: e.target.checked ? 'muelle' : 'muelle',
                })} />
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                  Pago parcial (abono + saldo en muelle / cupo)
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: '#78716c' }}>
                  Marca si la agencia recibió solo una parte por {labelMetodo} y el resto se cobra al embarcar o queda en cupo.
                </Typography>
              </Box>
            </Box>
            {pago.transferencia_parcial_check && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <TextField size="small" type="text" label={`Monto recibido por la agencia (${labelMetodo})`}
                  value={pago.monto_transferido ? String(pago.monto_transferido) : ''}
                  onChange={e => {
                    const clean = e.target.value.replace(/[^0-9]/g, '');
                    setPago({ ...pago, monto_transferido: clean });
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  error={excedido || muyAlto || comExcedeMt}
                  helperText={
                    excedido
                      ? `⚠️ El monto recibido supera el precio total ($${precioTotal.toLocaleString('es-CO')})`
                      : comExcedeMt
                        ? `⚠️ La comisión retenida ($${comAnt.toLocaleString('es-CO')}) no puede ser mayor a lo recibido por la agencia ($${mt.toLocaleString('es-CO')})`
                        : muyAlto
                          ? '⚠️ Si el cliente entregó todo el precio, desactiva este switch (no es parcial)'
                          : `Precio total: $${precioTotal.toLocaleString('es-CO')}${comAnt > 0 ? `. De ese monto, la agencia retiene $${comAnt.toLocaleString('es-CO')} de comisión.` : ''}`
                  } />
                <TextField size="small" select label="Saldo a cargo de *"
                  value={pago.saldo_a_cargo_de}
                  onChange={e => setPago({ ...pago, saldo_a_cargo_de: e.target.value })}>
                  <MenuItem value="muelle">🏖️ Turista paga en muelle al embarcar</MenuItem>
                  <MenuItem value="agencia">📒 Agencia (queda contra su cupo de crédito)</MenuItem>
                </TextField>
                {!excedido && !muyAlto && !comExcedeMt && saldoMuelle > 0 && (
                  <Alert severity="info" sx={{ fontSize: 11.5 }}>
                    {pago.saldo_a_cargo_de === 'muelle' ? (
                      <>🏖️ Saldo a cobrar al turista en muelle: <strong>${saldoMuelle.toLocaleString('es-CO')}</strong></>
                    ) : (
                      <>📒 Saldo contra cupo de {agencia.nombre}: <strong>${saldoMuelle.toLocaleString('es-CO')}</strong></>
                    )}
                  </Alert>
                )}
              </Stack>
            )}
          </Box>
        );
      })()}

      {/* Cupo de la agencia cuando aplica (credito/muelle). Lazy-loaded al
          seleccionar la forma de pago — evita query si no se necesita. */}
      {agencia && (pago.forma_pago === 'credito' || pago.forma_pago === 'muelle') && (
        <CupoAgenciaInfo
          agenciaId={agencia.id}
          agenciaNombre={agencia.nombre}
          precioRequerido={precioTotal - (Number(pago.comision_anticipada) || 0)}
          formaPago={pago.forma_pago}
        />
      )}

      {/* Pago DIRECTO cliente → Entretur (legacy) — REEMPLAZADO por la caja
          "🧮 Distribución del pago". Ocultado para eliminar redundancia. */}
      {false && agencia && !esCortesia100 && !pago.mixto_check && (
        <Box sx={{ p: 1.25, border: '1px solid #bfdbfe', borderRadius: 2, bgcolor: '#eff6ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch size="small" checked={pago.pago_directo_check}
              onChange={e => setPago({
                ...pago,
                pago_directo_check: e.target.checked,
                // Al desactivar, limpiar campos para no enviar basura
                ...(e.target.checked ? {} : {
                  pago_directo_monto: 0,
                  pago_directo_referencia: '',
                  pago_directo_comprobante_url: '',
                  pago_directo_cuenta_destino_id: '',
                }),
              })} />
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a' }}>
                Pago DIRECTO del cliente a Entretur
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: '#3b82f6' }}>
                Además de lo que le pagó a {agencia.nombre}. Ej: cliente le transfirió $X directo a Entretur.
              </Typography>
            </Box>
          </Box>
          {pago.pago_directo_check && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField size="small" type="text" label="Monto que el cliente transfirió *"
                  value={pago.pago_directo_monto ? String(pago.pago_directo_monto) : ''}
                  onChange={e => {
                    const clean = e.target.value.replace(/[^0-9]/g, '');
                    setPago({ ...pago, pago_directo_monto: clean });
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                <TextField size="small" select label="Método *"
                  value={pago.pago_directo_metodo}
                  onChange={e => setPago({ ...pago, pago_directo_metodo: e.target.value, pago_directo_cuenta_destino_id: '' })}>
                  <MenuItem value="transferencia">🏦 Transferencia</MenuItem>
                  <MenuItem value="nequi">📲 Nequi</MenuItem>
                  <MenuItem value="wompi">📲 Wompi</MenuItem>
                  <MenuItem value="bold_link">💳 Bold</MenuItem>
                  <MenuItem value="efectivo">💵 Efectivo</MenuItem>
                  <MenuItem value="tarjeta">💳 Tarjeta</MenuItem>
                </TextField>
              </Box>
              {CUENTA_TIPO_POR_FORMA[pago.pago_directo_metodo] && (() => {
                const tiposOk = tiposDeCuentaPara(pago.pago_directo_metodo);
                const cuentasFiltradas = (cuentasDestino || []).filter(c => tiposOk.includes(c.tipo));
                return (
                  <TextField size="small" select label="Cuenta destino Entretur *"
                    value={pago.pago_directo_cuenta_destino_id}
                    onChange={e => setPago({ ...pago, pago_directo_cuenta_destino_id: e.target.value })}>
                    <MenuItem value="">— Selecciona la cuenta —</MenuItem>
                    {cuentasFiltradas.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.nombre}{c.titular ? ` · ${c.titular}` : ''}</MenuItem>
                    ))}
                  </TextField>
                );
              })()}
              {['transferencia', 'tarjeta'].includes(pago.pago_directo_metodo) && (
                <TextField size="small" label={pago.pago_directo_metodo === 'tarjeta' ? 'Autorización datáfono *' : 'Referencia transferencia *'}
                  value={pago.pago_directo_referencia}
                  onChange={e => setPago({ ...pago, pago_directo_referencia: e.target.value })} />
              )}
              <TextField size="small" fullWidth label="URL comprobante (opcional)"
                value={pago.pago_directo_comprobante_url}
                onChange={e => setPago({ ...pago, pago_directo_comprobante_url: e.target.value })}
                placeholder="https://…"
                sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
            </Stack>
          )}
        </Box>
      )}

      {/* Hint para guiar al operador cuando elige "Crédito agencia": si en
          realidad la agencia SÍ cobró algo del cliente, conviene elegir el
          método físico + Pago parcial + Comisión anticipada para que la
          descomposición quede explícita en el resumen. */}
      {agencia && pago.forma_pago === 'credito' && (
        <Alert severity="info" sx={{ fontSize: 11.5 }}>
          ℹ️ Elegiste <b>Crédito agencia</b>: todo el precio queda como deuda de {agencia.nombre} a Entretur.
          <br/>
          Si la agencia <b>sí cobró algo</b> del cliente (efectivo, transferencia, etc.) y solo una parte queda a crédito,
          mejor elige el método físico (Efectivo / Transferencia / Tarjeta / Nequi) y activa <b>Pago parcial</b>
          con <b>Comisión anticipada</b> para que el resumen muestre la descomposición.
        </Alert>
      )}

      {/* Soporte de pago — solo en VENTA DIRECTA (sin agencia). Con agencia,
          los comprobantes se suben dentro del Canal 2 (una imagen por pago
          directo). Se oculta también en bold_link (Bold genera su propio
          comprobante). */}
      {requiereSoporte && !agencia && (
        <Box sx={{ p: 1.25, border: '1px dashed #cbd5e1', borderRadius: 2, bgcolor: '#f8fafc' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 0.75 }}>
            📎 Soporte de pago {pago.forma_pago === 'efectivo' ? '(opcional)' : '(recomendado)'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button component="label" size="small" variant="outlined" disabled={subiendo}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              {subiendo ? 'Subiendo…' : (pago.comprobante_url ? 'Cambiar archivo' : 'Subir archivo')}
              <input type="file" hidden accept=".pdf,image/*"
                // key fuerza re-render del input → permite re-subir el mismo
                // archivo después de borrarlo (sin esto el input recuerda el
                // último archivo y el onChange no dispara).
                key={pago.comprobante_url || 'empty'}
                onChange={e => handleUpload(e.target.files?.[0])} />
            </Button>
            {pago.comprobante_url && (
              <>
                <Typography sx={{ fontSize: 11, color: '#16a34a', flex: 1,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ✓ {nombreArchivo || 'Archivo subido'}
                </Typography>
                <Tooltip title="Quitar comprobante (podés subir otro)">
                  <IconButton size="small" disabled={subiendo}
                    onClick={() => {
                      setPago({ ...pago, comprobante_url: '' });
                      setNombreArchivo('');
                    }}
                    sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
          <TextField size="small" fullWidth label="o pega la URL del comprobante"
            value={pago.comprobante_url}
            onChange={e => { setPago({ ...pago, comprobante_url: e.target.value }); setNombreArchivo(''); }}
            placeholder="https://…"
            sx={{ mt: 1, '& .MuiInputBase-input': { fontSize: 12 } }} />
        </Box>
      )}

      {pago.forma_pago === 'bold_link' && (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          La reserva se creará en HOLD. Se generará un link Bold que puedes copiar al cliente.
          Cuando llegue el webhook de pago, la reserva pasa a confirmada.
        </Alert>
      )}

      {/* Comisión anticipada + Descuento — solo si hay agencia y comisión > 0.
          El toggle de comisión anticipada quedó integrado en la caja verde
          "🧮 Distribución del pago" de arriba, aquí solo mantenemos el bloque
          de descuento (concepto distinto: la agencia regala parte de SU
          comisión al turista → sale del bolsillo de la agencia, no de Entretur). */}
      {agencia && comisionTotal > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          {/* Toggle Comisión anticipada — REEMPLAZADO por la caja verde. */}
          {false && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Switch checked={pago.comision_anticipada_check}
              onChange={e => setPago({
                ...pago,
                comision_anticipada_check: e.target.checked,
                comision_anticipada: e.target.checked ? comisionTotal : 0,
              })} />
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                {agencia.nombre} ya cobró su comisión por anticipado
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                Marca esto si la agencia se quedó con su comisión cobrándola directo al cliente.
              </Typography>
            </Box>
          </Box>
          )}
          {false && pago.comision_anticipada_check && (() => {
            // Tope dinámico: comisión total menos el descuento sobre comisión actual.
            // El backend valida (com_ant + desc_com ≤ com_total), así que mantenemos la regla aquí.
            const descCom = pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0;
            const topeAnticipo = Math.max(comisionTotal - descCom, 0);
            const montoAnt = Number(pago.comision_anticipada) || 0;
            const excedido = montoAnt > topeAnticipo + 0.01;
            const queRecibe = Math.max(precioTotal - montoAnt, 0);
            const deudaAgencia = Math.max(comisionTotal - montoAnt - descCom, 0);
            return (
              <Stack spacing={1}>
                <TextField size="small" type="text" label="Monto cobrado por anticipado"
                  value={pago.comision_anticipada ? String(pago.comision_anticipada) : ''}
                  onChange={e => {
                    const clean = e.target.value.replace(/[^0-9]/g, '');
                    setPago({ ...pago, comision_anticipada: clean });
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  error={excedido}
                  helperText={
                    excedido
                      ? `⚠️ Excede el tope: máximo $${topeAnticipo.toLocaleString('es-CO')}`
                      : `Comisión total: $${comisionTotal.toLocaleString('es-CO')}. Tope (menos descuento): $${topeAnticipo.toLocaleString('es-CO')}.`
                  } />
                {!excedido && montoAnt > 0 && (
                  <Alert severity="success" sx={{ fontSize: 11.5 }}>
                    <strong>{agencia.nombre} ya se quedó con ${montoAnt.toLocaleString('es-CO')}</strong> de comisión.<br/>
                    Cliente paga al operador: <strong>${queRecibe.toLocaleString('es-CO')}</strong>.<br/>
                    Comisión pendiente por liquidar a la agencia: <strong>${deudaAgencia.toLocaleString('es-CO')}</strong>.
                  </Alert>
                )}
              </Stack>
            );
          })()}

          {/* Descuento sobre comisión — movido al Canal 1 (dentro de la
              distribución del pago). Ya no se muestra aquí para evitar
              redundancia con el toggle "La agencia ofrece descuento" del C1. */}
        </>
      )}

      {/* Panel de resumen visual (legacy) — REEMPLAZADO por la caja
          "🧮 Distribución del pago" que aparece arriba. Ambos paneles hacían
          lo mismo pero se descuadraban entre sí cuando se activaba pago_directo
          (el legacy asumía monto_agencia = precio si no había parcial → suma
          duplicada). Solo se muestra en casos edge donde la caja nueva no
          aplica (nunca por ahora — reservado para futuro). */}
      {false && agencia && !pago.mixto_check && !esCortesia100 && precioTotal > 0 && (() => {
        const formaFisica = ['transferencia', 'efectivo', 'tarjeta', 'nequi'].includes(pago.forma_pago);
        const formaCredito = pago.forma_pago === 'credito';
        const formaMuelle = pago.forma_pago === 'muelle';
        const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
        const descCom = pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0;
        const esParcial = pago.transferencia_parcial_check;
        const objetivo = Math.max(precioTotal - descCom, 0);

        // Lo que entrega el cliente a la agencia en este momento (método físico)
        const clienteEntregaAgencia = formaFisica
          ? (esParcial ? (Number(pago.monto_transferido) || 0) : objetivo)
          : 0;
        // Pago DIRECTO del cliente a Entretur (flujo paralelo al agencia↔Entretur)
        const pagoDirectoEntretur = pago.pago_directo_check ? (Number(pago.pago_directo_monto) || 0) : 0;
        // Lo que paga el turista en el muelle al embarcar
        const turistaMuelle = formaMuelle
          ? Math.max(objetivo - pagoDirectoEntretur, 0)
          : (formaFisica && esParcial && pago.saldo_a_cargo_de === 'muelle'
              ? Math.max(objetivo - clienteEntregaAgencia - pagoDirectoEntretur, 0)
              : 0);
        // Lo que queda contra cupo de crédito de la agencia
        const aCreditoAgencia = formaCredito
          ? Math.max(objetivo - pagoDirectoEntretur, 0)
          : (formaFisica && esParcial && pago.saldo_a_cargo_de === 'agencia'
              ? Math.max(objetivo - clienteEntregaAgencia - pagoDirectoEntretur, 0)
              : 0);
        const deudaEntretur = Math.max(clienteEntregaAgencia - comAnt, 0) + aCreditoAgencia;

        const suma = clienteEntregaAgencia + pagoDirectoEntretur + turistaMuelle + aCreditoAgencia;
        const cuadra = Math.abs(suma - objetivo) < 0.5;
        const metodoLabel = ({
          transferencia: 'transferencia', efectivo: 'efectivo',
          tarjeta: 'tarjeta', nequi: 'Nequi',
        })[pago.forma_pago] || pago.forma_pago;

        return (
          <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#0c4a6e', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              💰 Resumen del pago
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography sx={{ fontSize: 11.5, color: '#1e293b', fontWeight: 600 }}>Precio total</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0c4a6e' }}>{formatPrecio(precioTotal)}</Typography>
            </Box>
            {descCom > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: 10.5, color: '#92400e' }}>
                  − Descuento que la agencia regala (de su comisión)
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>−{formatPrecio(descCom)}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 0.5, borderColor: '#bae6fd' }} />

            {clienteEntregaAgencia > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 11.5, color: '#1e293b' }}>
                    ✋ Cliente entrega a {agencia.nombre} ({metodoLabel})
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>
                    {formatPrecio(clienteEntregaAgencia)}
                  </Typography>
                </Box>
                <Box sx={{ pl: 1.5, mb: 0.5 }}>
                  {comAnt > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>↳ 🟡 Comisión que retiene la agencia</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{formatPrecio(comAnt)}</Typography>
                    </Box>
                  )}
                  {(clienteEntregaAgencia - comAnt) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>↳ 📒 Crédito agencia → Entretur (deuda)</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{formatPrecio(clienteEntregaAgencia - comAnt)}</Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
            {pagoDirectoEntretur > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: 11.5, color: '#1e293b' }}>
                  🏦 Cliente transfirió directo a Entretur ({pago.pago_directo_metodo})
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>
                  {formatPrecio(pagoDirectoEntretur)}
                </Typography>
              </Box>
            )}
            {turistaMuelle > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: 11.5, color: '#1e293b' }}>
                  🏖️ Turista paga en muelle al embarcar
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0284c7' }}>{formatPrecio(turistaMuelle)}</Typography>
              </Box>
            )}
            {aCreditoAgencia > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: 11.5, color: '#1e293b' }}>
                  📒 Crédito agencia (queda en cupo)
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#a16207' }}>{formatPrecio(aCreditoAgencia)}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 0.5, borderColor: '#bae6fd' }} />
            {deudaEntretur > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: 11, color: '#0c4a6e', fontWeight: 700 }}>
                  Total que {agencia.nombre} debe a Entretur
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0c4a6e' }}>{formatPrecio(deudaEntretur)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
              <Typography sx={{ fontSize: 10.5, color: cuadra ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                {cuadra
                  ? '✓ Cuadra exacto contra el precio'
                  : `⚠️ Suma ${formatPrecio(suma)} vs objetivo ${formatPrecio(objetivo)}`}
              </Typography>
            </Box>
          </Box>
        );
      })()}

      {/* Hint cuando es venta directa de Entretur: el descuento solo se da
          sobre la comisión, igual que con agencias terceras. Si no hay agencia
          seleccionada el wizard NO ofrece descuento. */}
      {!agencia && (
        <Alert severity="info" sx={{ fontSize: 11.5 }}>
          ℹ️ Para aplicar un descuento, selecciona <strong>ENTRETUR</strong> (o la agencia
          correspondiente) en el paso anterior. El descuento siempre sale de la
          comisión — sin agencia, no hay comisión de la que descontar.
        </Alert>
      )}
    </Stack>
  );
}

function StepConfirmar({ tipo, salidaSel, alquilerSel, fecha, form, pago, precioTotal, comisionTotal, agencia, cuentasDestino = null }) {
  const paxTotal = (Number(form.adultos) || 0) + (Number(form.ninos) || 0) + (Number(form.bebes) || 0);
  // Único descuento posible: el que la agencia (tercera o Entretur) regala al
  // turista de su propia comisión. Reduce lo que paga el cliente y la deuda
  // de comisión que Entretur le debe a la agencia al liquidar.
  const descComision = pago.descuento_comision_check ? (Number(pago.descuento_comision) || 0) : 0;
  const totalFinal = Math.max(precioTotal - descComision, 0);
  return (
    <Stack spacing={1.5}>
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
        <Typography sx={{ fontSize: 11, color: '#0d9488', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
          Salida
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {tipo === 'pasadia' ? (salidaSel?.paquete_nombre || salidaSel?.paquete?.nombre) : alquilerSel?.nombre} · {fecha}
        </Typography>
      </Box>

      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
          Cliente
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{form.nombre_cliente}</Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>📱 {form.numero_whatsapp}</Typography>
        {form.correo && <Typography sx={{ fontSize: 11, color: '#64748b' }}>✉️ {form.correo}</Typography>}
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
          {form.adultos} adulto(s) · {form.ninos} niño(s) · {form.bebes} bebé(s)
        </Typography>
      </Box>

      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fefce8', border: '1px solid #fef08a' }}>
        <Typography sx={{ fontSize: 11, color: '#854d0e', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
          Pago
        </Typography>
        <Typography sx={{ fontSize: 12 }}>{FORMA_PAGO.find(f => f.value === pago.forma_pago)?.label}</Typography>
        {pago.referencia_pago && (
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>Ref: {pago.referencia_pago}</Typography>
        )}
        {CUENTA_TIPO_POR_FORMA[pago.forma_pago] && pago.cuenta_destino_id && (() => {
          const c = (cuentasDestino || []).find(x => String(x.id) === String(pago.cuenta_destino_id));
          if (!c) return null;
          const iconoPorTipo = { nequi: '📲', wompi: '📲', bancolombia: '🏦', banco_bogota: '🏦', bold: '💳', datafono: '💳' };
          return (
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              {iconoPorTipo[c.tipo] || '💰'} Cuenta: <strong>{c.nombre}</strong>{c.titular ? ` · ${c.titular}` : ''}
            </Typography>
          );
        })()}
      </Box>

      <Divider />

      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
        <Row label="Subtotal" value={formatPrecio(precioTotal)} />
        {descComision > 0 && agencia && (
          <Row label={`Descuento que regala ${agencia.nombre} (de su comisión)`}
               value={`- ${formatPrecio(descComision)}`} color="#a16207" />
        )}
        {agencia && comisionTotal > 0 && (() => {
          const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
          // La comisión que Entretur le debe a la agencia se reduce por la
          // comisión anticipada (ya en sus manos) y por el descuento que la
          // agencia regaló al turista de su propia comisión. NO se reduce por
          // descuento_comercial (eso sale del bolsillo de Entretur, no de la comisión).
          const deuda = Math.max(comisionTotal - comAnt - descComision, 0);
          if (comAnt > 0 || descComision > 0) {
            return (
              <>
                <Row label={`Comisión ${agencia.nombre}`} value={formatPrecio(comisionTotal)} color="#be185d" />
                {comAnt > 0 && (
                  <Row label={`Cobrada por ${agencia.nombre}`} value={`- ${formatPrecio(comAnt)}`} color="#16a34a" />
                )}
                {descComision > 0 && (
                  <Row label={`Regalada al turista`} value={`- ${formatPrecio(descComision)}`} color="#a16207" />
                )}
                <Row label={`Pendiente liquidar a ${agencia.nombre}`} value={formatPrecio(deuda)} color={deuda > 0 ? '#be185d' : '#16a34a'} />
              </>
            );
          }
          return <Row label={`Comisión ${agencia.nombre}`} value={formatPrecio(comisionTotal)} color="#be185d" />;
        })()}
        <Divider sx={{ my: 0.75 }} />
        {pago.forma_pago === 'credito' && agencia ? (() => {
          // Crédito a agencia: el cliente NO paga a Entretur. La agencia le vende
          // al PVP y queda debiendo a Entretur el NETO (PVP − comisión efectiva),
          // independientemente de si la comisión fue anticipada o se retiene al
          // liquidar — el neto por cobrar a la agencia es el mismo.
          const comisionEfectiva = Math.max(comisionTotal - descComision, 0);
          const netoPorCobrar = Math.max(totalFinal - comisionEfectiva, 0);
          return (
            <>
              <Row label={`PVP (cobra ${agencia.nombre} al cliente)`} value={formatPrecio(totalFinal)} color="#64748b" />
              <Row label={`POR COBRAR A ${agencia.nombre.toUpperCase()} (crédito)`}
                   value={formatPrecio(netoPorCobrar)} bold large color="#be185d" />
              <Typography sx={{ fontSize: 11, color: '#64748b', textAlign: 'right', mt: 0.25 }}>
                = PVP {formatPrecio(totalFinal)} − comisión efectiva {formatPrecio(comisionEfectiva)}
              </Typography>
            </>
          );
        })() : (() => {
          // Cuando hay agencia externa que retiene su comisión (comAnt > 0), el
          // cliente le paga esos $ directamente a la agencia (no a Entretur).
          // El "total a cobrar al cliente" desde el punto de vista de Entretur
          // es el NETO: PVP − descComisión − comisiónAnticipada.
          // Si no hay agencia (o la agencia no retiene comisión), Entretur cobra
          // el PVP completo (menos descComisión si aplica).
          const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
          const hayAgenciaConComAnt = !!agencia && comAnt > 0;
          const netoAEntretur = Math.max(totalFinal - comAnt, 0);
          const pagoDirecto = (pago.pago_directo_check ? (Number(pago.pago_directo_monto) || 0) : 0)
            + (pago.pagos_directos_extras || []).reduce((acc, x) => acc + (Number(x.monto) || 0), 0);
          if (hayAgenciaConComAnt) {
            return (
              <>
                <Row label="PVP total (paga el cliente)" value={formatPrecio(totalFinal)} color="#64748b" />
                <Row label={`Comisión retenida por ${agencia.nombre}`} value={`- ${formatPrecio(comAnt)}`} color="#be185d" />
                <Row label="TOTAL neto a cobrar por Entretur" value={formatPrecio(netoAEntretur)} bold large color="#0d9488" />
                {pagoDirecto > 0 && (
                  <Typography sx={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>
                    Ya cobrado directo al cliente: {formatPrecio(pagoDirecto)} ·
                    {' '}Pendiente: {formatPrecio(Math.max(netoAEntretur - pagoDirecto, 0))}
                  </Typography>
                )}
              </>
            );
          }
          return (
            <Row label="TOTAL a cobrar al cliente" value={formatPrecio(totalFinal)} bold large color="#0d9488" />
          );
        })()}
        {/* Aclarar momento de cobro cuando es muelle (paridad con el chatbot,
            que sí lo dice). Sin esto el operador olvida comunicar al cliente que
            el cobro NO es ahora sino al embarcar. */}
        {pago.forma_pago === 'muelle' && (() => {
          const saldoMuelle = Math.max(totalFinal - (Number(pago.comision_anticipada) || 0), 0);
          return (
            <Alert severity="warning" icon={false} sx={{ mt: 1, fontSize: 12, '& .MuiAlert-message': { width: '100%' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <span>🏖️ <strong>El turista paga en muelle al embarcar</strong></span>
                <strong>{formatPrecio(saldoMuelle)}</strong>
              </Box>
            </Alert>
          );
        })()}
        {/* Desglose del flujo: la agencia recibió BRUTO del cliente, retuvo
            comisión, transfirió NETO a Entretur, y (si aplica) queda saldo por
            cobrar al turista en muelle. Fórmula:
              neto_a_entretur = mt − comAnt
              saldo_muelle    = precioTotal − mt
            NO restamos comAnt del saldo — la comisión ya está DENTRO del bruto
            que la agencia recibió, no fuera. Restar dos veces era el bug. */}
        {pago.forma_pago === 'transferencia' && pago.transferencia_parcial_check && (() => {
          const mt = Number(pago.monto_transferido) || 0;
          const comAnt = pago.comision_anticipada_check ? (Number(pago.comision_anticipada) || 0) : 0;
          const pagoDirecto = (pago.pago_directo_check ? (Number(pago.pago_directo_monto) || 0) : 0)
          + (pago.pagos_directos_extras || []).reduce((acc, x) => acc + (Number(x.monto) || 0), 0);
          const netoViaAgencia = Math.max(mt - comAnt, 0);
          const netoAEntretur = netoViaAgencia + pagoDirecto;
          // Saldo muelle = precio − todo lo que ya entró (via agencia + directo).
          const saldoMuelle = Math.max(totalFinal - mt - pagoDirecto, 0);
          if (mt <= 0 && pagoDirecto <= 0 && saldoMuelle <= 0) return null;
          return (
            <Alert severity={saldoMuelle > 0 && pago.saldo_a_cargo_de === 'muelle' ? 'warning' : 'info'} icon={false}
              sx={{ mt: 1, fontSize: 12, '& .MuiAlert-message': { width: '100%' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                <span>🏦 Recibido por la agencia (bruto del cliente)</span>
                <strong>{formatPrecio(mt)}</strong>
              </Box>
              {comAnt > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.25, color: '#64748b' }}>
                  <span>&nbsp;&nbsp;└ Comisión retenida por la agencia</span>
                  <span>− {formatPrecio(comAnt)}</span>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: pagoDirecto > 0 ? 0.25 : 0.5, fontWeight: 700, color: '#0f766e' }}>
                <span>💸 Neto transferido a Entretur (vía agencia)</span>
                <strong>{formatPrecio(netoViaAgencia)}</strong>
              </Box>
              {pagoDirecto > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.25, fontWeight: 700, color: '#0f766e' }}>
                    <span>💸 Pago directo cliente → Entretur</span>
                    <strong>{formatPrecio(pagoDirecto)}</strong>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5, borderTop: '1px solid #99f6e4', pt: 0.25, fontWeight: 800, color: '#0f766e' }}>
                    <span>= Total a Entretur</span>
                    <strong>{formatPrecio(netoAEntretur)}</strong>
                  </Box>
                </>
              )}
              {saldoMuelle > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, borderTop: '1px dashed #cbd5e1', pt: 0.5 }}>
                  {pago.saldo_a_cargo_de === 'muelle' ? (
                    <>
                      <span>🏖️ <strong>Saldo por cobrar al turista en muelle</strong></span>
                      <strong>{formatPrecio(saldoMuelle)}</strong>
                    </>
                  ) : (
                    <>
                      <span>📒 <strong>Saldo contra cupo de {agencia?.nombre || 'la agencia'}</strong></span>
                      <strong>{formatPrecio(saldoMuelle)}</strong>
                    </>
                  )}
                </Box>
              )}
            </Alert>
          );
        })()}
      </Box>
    </Stack>
  );
}

function Row({ label, value, bold, large, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography sx={{ fontSize: large ? 14 : 12, fontWeight: bold ? 800 : 500, color: '#475569' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: large ? 16 : 12, fontWeight: bold ? 800 : 600, color: color || '#0f172a' }}>
        {value}
      </Typography>
    </Box>
  );
}

function ResultPanel({ resultado }) {
  const copy = (t) => navigator.clipboard?.writeText(t).catch(() => {});
  return (
    <Stack spacing={2} sx={{ textAlign: 'center', py: 2 }}>
      <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981', mx: 'auto' }} />
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
        Reserva creada exitosamente
      </Typography>
      {/* Si se pidió usar el saldo a favor, el resultado se confirma acá
          mismo: es lo que el operador acaba de pedir. */}
      {resultado.saldo_favor_aplicado > 0 && (
        <Typography sx={{ fontSize: 12.5, color: '#15803d', fontWeight: 700 }}>
          💚 Se aplicaron {formatPrecio(resultado.saldo_favor_aplicado)} del saldo a favor de la agencia
        </Typography>
      )}
      {resultado.aviso_saldo_favor && (
        <Typography sx={{ fontSize: 12, color: '#b45309' }}>
          El saldo a favor no se aplicó: {resultado.aviso_saldo_favor}. Sigue disponible
          desde la pestaña de Movimientos.
        </Typography>
      )}
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', mx: 4 }}>
        <Typography sx={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>
          Código
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: '#166534' }}>
            {resultado.codigo_reserva}
          </Typography>
          <Tooltip title="Copiar">
            <IconButton size="small" onClick={() => copy(resultado.codigo_reserva)}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {resultado.payment_url && (
        <Stack spacing={1} sx={{ mx: 2 }}>
          <Alert severity="info" sx={{ fontSize: 12, textAlign: 'left' }}>
            Link de pago Bold generado. Compártelo con el cliente.
          </Alert>
          <Button variant="outlined" startIcon={<ContentCopyIcon />}
            onClick={() => copy(resultado.payment_url)}
            sx={{ textTransform: 'none' }}>
            Copiar link
          </Button>
          <Button variant="outlined"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Tu link de pago: ${resultado.payment_url}`)}`, '_blank')}
            sx={{ textTransform: 'none' }}>
            Enviar por WhatsApp
          </Button>
        </Stack>
      )}
    </Stack>
  );
}


/**
 * CupoAgenciaInfo — Muestra cupo disponible vs requerido en vivo.
 *
 * Aparece solo cuando la forma de pago carga al cupo (credito o muelle).
 * Hace lazy fetch al endpoint /turismo/agencias/{id}/cupo-disponible.
 * Re-fetchea cuando cambia la agencia. Muestra alert rojo si requerido > disponible.
 */
function CupoAgenciaInfo({ agenciaId, agenciaNombre, precioRequerido, formaPago }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!agenciaId) return;
    let cancel = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/turismo/agencias/${agenciaId}/cupo-disponible`,
      { headers: getAuthHeaders() })
      .then(async r => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || 'Error');
        }
        return r.json();
      })
      .then(d => { if (!cancel) setData(d); })
      .catch(e => { if (!cancel) setError(e.message); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [agenciaId]);

  if (loading) {
    return (
      <Alert severity="info" icon={<CircularProgress size={14} />} sx={{ fontSize: 11 }}>
        Consultando cupo de {agenciaNombre}…
      </Alert>
    );
  }
  if (error) {
    return <Alert severity="warning" sx={{ fontSize: 11 }}>{error}</Alert>;
  }
  if (!data) return null;

  const requerido = Math.max(precioRequerido || 0, 0);
  const excede = requerido > data.disponible + 0.01;
  const severity = excede ? 'error' : (requerido > 0 ? 'success' : 'info');
  const titulo = formaPago === 'credito'
    ? '💳 Crédito agencia'
    : '⚓ Pago en muelle (cupo se carga hasta cobro)';

  return (
    <Alert severity={severity} sx={{ fontSize: 11.5 }}>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.25 }}>
          {titulo} — {agenciaNombre}
        </Typography>
        <Typography sx={{ fontSize: 11 }}>
          Cupo disponible: <strong>{formatPrecio(data.disponible)}</strong> de {formatPrecio(data.cupo_total)}
          {' '}(usado: {formatPrecio(data.usado)})
        </Typography>
        {requerido > 0 && (
          <Typography sx={{ fontSize: 11, mt: 0.25 }}>
            Esta reserva requiere: <strong>{formatPrecio(requerido)}</strong>
          </Typography>
        )}
        {excede && (
          <Typography sx={{ fontSize: 11, mt: 0.5, fontWeight: 700 }}>
            ⚠️ El cupo disponible no alcanza para esta reserva. Cambia la forma de pago
            (transferencia, tarjeta) o pide al admin liquidar parte del cupo de {agenciaNombre}.
          </Typography>
        )}
      </Box>
    </Alert>
  );
}
